// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Contrato do mapa — a forma compacta que atravessa a rede
// ============================================================================
//
// PORQUE ESTE FICHEIRO ESTÁ NUM PACOTE PARTILHADO
//   A forma compacta é um contrato entre quem a produz (API) e quem a consome
//   (mapa). Se cada lado declarasse o seu próprio tipo, inserir um campo no meio
//   do *tuple* no servidor não daria erro de compilação no cliente — este
//   passaria a ler índices errados **em silêncio**. Com uma só declaração, o
//   TypeScript recusa a compilação de imediato.
//
//   Por D-042 este formato é **definitivo**, o que torna a garantia mais
//   valiosa do que o custo do pacote extra.
//
// REGRA DESTE PACOTE
//   Nada de `node:*`, nada de DOM, nada de dependências. O `tsconfig` tem
//   `"types": []` justamente para tornar essa regra verificável pelo compilador
//   em vez de a deixar à disciplina de quem edita.
//
// PORQUE SE ENVIA O ANO INTEIRO DE UMA VEZ
//   MEDIDO com os 90 208 registos do gerador: **3,08 MB** de JSON, **0,48 MB**
//   comprimido com gzip. É o payload de um site com duas fotografias, e paga-se
//   uma vez ao abrir o plano. Enviar tudo e janelar no cliente é o que o *spike*
//   mediu (23,5–24,5 ms p95), e manter essa arquitectura é o que faz a medição
//   transferir-se para o produto.
//
//   ⚠️ Estes números são de UMA versão do contrato. Acrescentar um campo mede-se
//   outra vez: quando `estadoViatura` e `meio` entraram, o payload subiu de
//   2,56 para 3,08 MB. Um número medido que fica no comentário depois de o
//   contrato mudar deixa de ser medição e passa a ser folclore.
//
//   A alternativa — pedir cada janela ao servidor — acrescentaria latência de
//   rede a cada deslocamento, que o *spike* nunca mediu, e arriscaria o tecto de
//   500 ms (D-008) por razões alheias ao nosso código.
//
//   ⚠️ Isto vale para UM ano de UM plano. Com dez anos de histórico não serve —
//   nessa altura janela-se no servidor. Fica registado para não ser esquecido.
//
// FORMA COMPACTA
//   Índices inteiros em vez de strings repetidas: 250 × 365 células com nomes
//   repetidos passariam de 10 MB.
// ============================================================================
// ⚠️ A ÚNICA importação deste ficheiro, e é de propósito que seja pequena: `tipo-de-parceiro.ts`
//    não importa nada, logo não há ciclo. O `tipo` do subcontratado entra no contrato do mapa
//    (B1) e a normalização tem de ser a MESMA que os ecrãs usam — duas leituras do mesmo `CHECK`
//    divergiriam, e a que divergisse era a que ninguém corre.
import { tipoDoParceiro } from './tipo-de-parceiro.js';
import { dataDoIndice, dentroDaJanela, indiceDaData, janelaDoAno } from './janela-do-mapa.js';
// ⚠️ A constante e não a cadeia à mão: é a mesma disciplina de `CHAVE_DE_LER_EMAIL` — um nome
//    escrito de memória num dos leitores produz uma bandeira que nunca acende, e nada o denuncia.
import { EM_ESPERA, PARADO_NO_SERVICO } from './tipo-do-dia.js';
/**
 * Bandeiras por célula, em bits.
 *
 * Um inteiro em vez de oito booleanos: são 91 250 células, e cada campo a mais
 * multiplica-se por isso na serialização.
 *
 * ⚠️ Não há bandeira para «viatura indisponível»: essa informação é
 * `estadoViatura >= 0`. Uma bandeira a duplicar um campo que já existe é uma
 * bandeira que um dia deixa de concordar com ele.
 */
export const BANDEIRA = {
    MOVIMENTO_IN: 1,
    MOVIMENTO_OUT: 2,
    /**
     * Dia de rotação: chegada E partida no mesmo dia.
     *
     * É o objectivo O1 do projeto. Na folha actual são as células **pretas**, e os
     * planeadores já as pintam à mão — ou seja, já marcam manualmente os dias que
     * o algoritmo deve optimizar. Ver camada visual §4.
     *
     * ⚠️ Neste dia a **semântica das sublinhas inverte-se**: a sublinha 1 passa a
     * ser a CHEGADA do itinerário que termina e a sublinha 2 a PARTIDA do que
     * começa. É por isso que `movimentos` guarda o par separado, e não um texto
     * já junto: o mapa precisa dos dois lados em linhas diferentes.
     */
    ROTACAO: 4,
    RESERVA: 8,
    /** O «?» que os planeadores usam para registar intenção provisória (D-033). */
    DUVIDA: 16,
    PRIMEIRO_DIA: 32,
    ULTIMO_DIA: 64,
    /**
     * Dois condutores na MESMA viatura no MESMO dia, em simultâneo.
     *
     * Não é o mesmo que rotação sequencial (`ROTACAO`), e a diferença é legal, não
     * cosmética: a tripulação dupla cai no art. 4.º o) e no art. 8.º n.º 5 do
     * Reg. (CE) 561/2006 — 9 h de repouso em cada 30 h, em vez das 11 h em 24 h.
     * Medido: 95 células na folha real. Se o mapa as colapsasse numa só, o motor
     * de recomendação herdaria contadores errados.
     */
    TRIPULACAO_DUPLA: 128,
    /**
     * Dia marcado como rotação em que a rotação **não se sustenta**.
     *
     * O cliente apontou o caso: um dia negro seguido de dia livre não é rotação,
     * porque não há itinerário nenhum a partir. Medido nos dados sintéticos:
     * **915 de 6 405 dias de rotação (14,3 %) não têm partida no dia seguinte** —
     * uma em cada sete células negras estava errada.
     *
     * A rotação é agora **derivada** e não aceite do campo de origem. Onde a
     * marcação não se sustenta, `ROTACAO` é retirada e esta bandeira fica no seu
     * lugar: o mapa mostra o dia como chegada e assinala-o como suspeito. Corrigir
     * em silêncio seria pior — na folha real isto é provavelmente erro de registo,
     * e quem planeia tem de o ver.
     */
    ROTACAO_SUSPEITA: 256,
    /**
     * ⚠️ A localidade deste dia é **INFERIDA**, não registada.
     *
     * 📗 A coluna `origem_localizacao` existe desde a migração 001 exactamente para isto —
     * distingue localidade REGISTADA de INFERIDA, e é o que permite ao contador legal dizer
     * «não é possível documentar» em vez de «incumpre» (F-33: eram 58 % de falsos
     * incumprimentos).
     *
     * ⚠️ **Porque é que precisa de bandeira e não bastava a coluna:** uma reserva preenche
     * TODOS os dias — o cliente exigiu-o, e a folha real tem localidade em 94,1 % dos dias
     * de corrida — mas só conhece o primeiro e o último. Os do meio são onde a viatura
     * *estava*. Sem marcar a diferença, uma inferência nossa fica indistinguível de uma
     * decisão de um planeador, e a **pernoita** decide o alojamento pago (D-060) e a prova
     * do repouso semanal (art. 8.º n.º 8).
     */
    LOCAL_INFERIDO: 512,
    /**
     * ⚠️⚠️ **A VIATURA ESTÁ EM TRÂNSITO — `tipo_dia = 'vazio'` (2026-08-14).**
     *
     * 📗 Cliente, 2026-08-13: *«dias em vazio de viagem de volta à base têm que ter um registo em
     * trânsito… esses dias têm de aparecer o motorista alocado e em trânsito — importante indicar o
     * motorista alocado especificamente no mapa»*.
     *
     * ⚠️ **É uma BANDEIRA e não um campo do *tuple*, e a razão é o orçamento de bytes:** um 17.º
     * campo paga em **todas** as 91 250 células para servir ~3 % delas. O destino vive num mapa
     * **esparso** (`destinosDoTransito`), pela mesma razão que as notas e as horas da partida vivem.
     *
     * ⚠️⚠️ **E o dado já existia sem ninguém o ler.** `tipo_dia = 'vazio'` e `destino_vazio` entraram
     * na migração 013 — escrita **para isto** — e ficaram: o SQL do mapa **selecciona** `tipo_dia` e
     * `construirDadosMapa` nunca o usou; `destino_vazio` nem era seleccionado. Medido nos dados
     * sintéticos: **2 839 dias `vazio`, ZERO com motorista e ZERO com horas de condução**. 📌 Um dia
     * em trânsito é um dia de TRABALHO — tem condutor, tem km e conta para o art. 8.º.
     */
    EM_TRANSITO: 1024,
    /**
     * ⚠️⚠️ **O ALFINETE — 📗 «um pisco que diga: ok, aqui já não quero mexer mais».**
     *
     * A coluna `alocacao.fixado` existe desde a **migração 029** («os travões»), com
     * `fixado_por`, `fixado_em` e `fixado_motivo`. O módulo `bloqueios.ts` sabe lê-la — e **o mapa
     * não a via**: o motor não podia distinguir um serviço que se pode deslocar de um que alguém
     * marcou para não mexer, porque essa informação não chegava à célula.
     *
     * 📌 É a peça que a **etapa 2** (retirar reservas) precisa antes de existir: sem ela, o motor
     * ofereceria deslocar exactamente aquilo que o planeador fixou.
     */
    FIXADO: 2048,
    /**
     * ⚠️⚠️ **ESTE DIA É DE OUTRO PLANO — ocupado, e não avariado** (D-1).
     *
     * 📗 Cliente, 2026-08-21: *«seleccionei o plano Açores, e em muitas linhas aparece a
     * ferramenta como se fosse oficina e diz "noutro plano". Qual é a lógica disto?»*
     *
     * ⚠️⚠️ **Antes disto, o bloco opaco pedia emprestado o `estado_viatura`** — punha-lhe a
     * cadeia `'Noutro plano'`. A intenção estava certa (o dia **tem** de ocupar, senão volta o 409
     * que o bloco opaco existe para evitar), mas o campo escolhido significa **indisponibilidade**:
     * oficina, avaria, sinistro. 📌 É «vestir a roupa de outra coisa», a quarta instância
     * registada neste projecto — e as consequências não eram cosméticas:
     *
     *   · o mapa desenhava a **chave inglesa** e a cor `grave`, e o planeador lia «avariada»;
     *   · ⚠️⚠️ o `substituicao.ts` abria o **«Quem substitui?»** ao clicar na célula, e esticava
     *     a «avaria» por todos os dias contíguos — o sistema oferecia-se para procurar substituto a
     *     um autocarro que está simplesmente **a trabalhar noutro plano**;
     *   · a legenda não tinha entrada nenhuma para isto.
     *
     * ✅ **Com uma bandeira própria, tudo isso se resolve de graça:** o desenho tem cor e ícone
     * próprios, a legenda ganha uma linha, o `substituicao.ts` exclui-a, e a distinção entre
     * *«avariada»* e *«a trabalhar noutro sítio»* volta a existir **em todos os sítios que a leem**.
     * ⚠️ Dizia «nos oito sítios», e o número não se reproduzia: depende de se contarem ficheiros ou
     * ocorrências, produto ou também provas e sondas. Um número que ninguém consegue confirmar
     * envelhece sem nada o denunciar — vale mais a frase sem ele.
     *
     * ⚠️ **E `diaLivreParaMobilizar` TEM de a ler.** É a única razão pela qual o campo emprestado
     * existia: sem ela, a célula opaca — sem cliente, sem itinerário e sem corrida — lê-se como uma
     * **paragem registada**, e uma paragem não ocupa de propósito (o `reservar.ts` escreve por cima
     * dela). O 409-depois-do-clique voltava no mesmo dia.
     */
    NOUTRO_PLANO: 4096,
    /**
     * ⚠️⚠️ **A VIATURA ESTÁ COMPROMETIDA E NÃO TRABALHA — `tipo_dia = 'parado_no_servico'`**
     * (migração `072`, 2026-09-08).
     *
     * 📗 §8.3: um serviço de 12 dias com 2 em Marrocos **não são 12 dias de trabalho**, *«e hoje
     * não há onde pôr isso no pedido»*. O grupo foi de ferry, o autocarro espera em Tarifa.
     *
     * ⚠️ **É uma BANDEIRA e não um campo do *tuple*, pela mesma razão do `EM_TRANSITO`:** um
     * campo novo paga em **todas** as 91 250 células para servir uma fracção delas. E **não traz
     * mapa esparso nenhum** — ao contrário do trânsito, este dia não tem destino: a viatura não
     * vai a sítio nenhum, e `localidade_id` já diz onde ela está (D-086).
     *
     * ⚠️⚠️ **NÃO É O QUE FAZ O DIA OCUPAR, e é importante não o ler assim.**
     * `diaLivreParaMobilizar` **não** lê esta bandeira, de propósito: o dia ocupa porque a linha
     * traz cliente, itinerário, corrida e pedido — ela **é** um dia daquele serviço. 📌 O
     * `NOUTRO_PLANO` precisou de ser lido lá porque chega **sem** essas colunas; este chega com
     * elas, logo acrescentá-la ao predicado era mudar o significado de um predicado com oito
     * chamadores para nada.
     *
     * ⚠️ **O que ela serve é o ECRÃ e a CONTA:** sem ela, a célula é indistinguível de um dia de
     * serviço normal, e o planeador que abre o mapa lê «12 dias de trabalho» onde há 10.
     */
    PARADO_NO_SERVICO: 8192,
    /**
     * ⚠️⚠️ **INÍCIO DE UM TROÇO ADMINISTRATIVO — `alocacao.inicio_de_troco`** (migração `085`,
     * 2026-09-14).
     *
     * 📗 Cliente: *«Viagens longas têm de ser divididas de 7 em 7 dias, só para já no caso de um
     * cliente grego.»*
     *
     * 📄 `docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md` §8 — «E · Partir de
     * 7 em 7 — e são DUAS partições». 📄 `apps/api/src/dividir-em-trocos.ts`.
     *
     * ⚠️⚠️ **NÃO É `dividir_viagem`.** Aquele muda a `referencia` — a viagem passa a ser DUAS,
     * com identidades diferentes. Este marca uma fronteira ADMINISTRATIVA **dentro da MESMA**
     * viagem: a `referencia` nunca muda, e é por isso que o mapa continua a desenhar **uma única
     * barra contínua** (a chave da barra é a referência/corrida — F-19,
     * `packages/dominio/src/mapa.ts` mais abaixo). Este sinal só diz «aqui começa outro troço de
     * reporte», sem partir nada.
     *
     * ⚠️⚠️ **A FORMA É A FRONTEIRA, NÃO O NÚMERO DO TROÇO** — decisão tomada ao desenhar a coluna,
     * e é o que torna «voltar a unir» trivial: apagar fronteiras (um só `UPDATE`) em vez de
     * renumerar N linhas. Um troço não tem identidade própria; só o dia em que **começa** é
     * marcado. O primeiro dia da viagem inteira NÃO leva esta bandeira — já tem `PRIMEIRO_DIA`, e
     * marcar aqui também seria a mesma informação duas vezes.
     *
     * ⚠️ **Num dia de rotação, esta bandeira é do DIA, não de uma das duas viagens** — tal como
     * `RESERVA` ou `DUVIDA`. A escrita só a liga à linha (`principal` ou `partida`) cuja
     * `referencia` é a que está a ser organizada em troços; qualquer uma das duas passagens deste
     * ficheiro que montam a célula pode acendê-la.
     */
    INICIO_DE_TROCO: 16384,
    /**
     * ⚠️⚠️ **A LOCALIDADE DESTE DIA É PRESUMIDA — `alocacao.local_presumido`** (migração `093`,
     * 2026-09-29).
     *
     * 📗 Cliente, sobre os pedidos SEM ponto de largada: *«quando não se sabe, o melhor é assumir
     * o do dia anterior e colocar um ponto de interrogação»*. A reserva grava no último dia a cidade
     * de `destinoFinalDoPedido` (qualidade `presumida`) e marca a linha; o mapa desenha-a com `?` e
     * o título `TITULO_DESTINO_PRESUMIDO` — a MESMA frase da ficha e da tira dos dias.
     *
     * ⚠️⚠️ **NÃO É `LOCAL_INFERIDO`.** Aquela diz «é onde a viatura ESTAVA» (os dias do meio de uma
     * reserva) e é lida pelo contador legal (F-33); esta diz «é onde o GRUPO deve terminar, segundo
     * o dia anterior». Vestir uma com o significado da outra era «vestir a roupa de outra coisa».
     *
     * ⚠️ **Bandeira e não campo do *tuple*, pela razão de sempre:** um 18.º campo pagava em todas
     * as 91 250 células para servir o último dia de uma fracção dos pedidos. E **não cria nó
     * nenhum** no ecrã: o `?` vai no MESMO texto da cidade (`rotuloDia`, `apps/web`).
     *
     * 📌 Só o que a origem declara, como `LOCAL_INFERIDO` e `INICIO_DE_TROCO`. Os CSV não têm a
     * coluna (a semente nunca presume), logo a ausência não acende nada.
     */
    LOCAL_PRESUMIDO: 32768,
};
/**
 * ⚠️ **A leitura da marca do local presumido (migração 093), num sítio só.** Há QUATRO sítios
 * neste ficheiro que põem a localidade de uma linha numa célula — a frota, a `partida` sozinha,
 * o subcontratado e a viagem em espera —, e a marca tem de ir com a cidade nos quatro: uma
 * célula que mostrasse a cidade sem o `?` punha o palpite a ler-se como facto. A guarda
 * `local-presumido-no-mapa.test.ts` conta-os (regra 8).
 */
export function bandeiraDoLocalPresumido(a) {
    return a['local_presumido'] === 'sim' ? BANDEIRA.LOCAL_PRESUMIDO : 0;
}
const ESTADOS = ['bom', 'aviso', 'grave', 'critico'];
function indexar(itens, chave) {
    const m = new Map();
    itens.forEach((t, i) => m.set(chave(t), i));
    return m;
}
/**
 * Índice do dia na JANELA, 0-based. Devolve -1 se a data não é legível ou cai fora da janela.
 * 📗 D-101 · era «no ano»; agora é na janela (que, na transição, é o próprio ano).
 */
function diaNaJanela(iso, j) {
    if (Number.isNaN(Date.parse(`${iso}T00:00:00Z`)))
        return -1;
    const i = indiceDaData(j, iso);
    return dentroDaJanela(j, i) ? i : -1;
}
/**
 * Constrói a forma compacta a partir das linhas da origem.
 *
 * Pura de propósito: não lê disco nem rede, e por isso é testável com dados em
 * memória e reutilizável quando a origem passar a ser o PostgreSQL.
 */
export function construirDadosMapa(
/** 📗 D-101 · a janela do mapa; um número é o ano civil inteiro (`janelaDoAno`). */
periodo, csv) {
    const janela = typeof periodo === 'number' ? janelaDoAno(periodo) : periodo;
    const ano = Number(janela.inicio.slice(0, 4));
    const nDias = janela.nDias;
    const fimDeSemana = [];
    const mesPorDia = [];
    const diaDoMes = [];
    for (let i = 0; i < nDias; i++) {
        const d = new Date(`${dataDoIndice(janela, i)}T00:00:00Z`);
        const semana = d.getUTCDay(); // 0 = domingo, 6 = sábado
        fimDeSemana.push(semana === 0 || semana === 6);
        mesPorDia.push(d.getUTCMonth());
        diaDoMes.push(d.getUTCDate());
    }
    const iCliente = indexar(csv.clientes, (c) => c['id'] ?? '');
    const iMotorista = indexar(csv.motoristas, (m) => m['id'] ?? '');
    const iLocalidade = indexar(csv.localidades, (l) => l['codigo_interno'] ?? '');
    const iViatura = indexar(csv.viaturas, (v) => v['id'] ?? '');
    // Os itinerários não têm tabela própria no CSV do mapa: recolhem-se das
    // alocações, na ordem em que aparecem.
    const iItinerario = new Map();
    const itinerarios = [];
    /**
     * `itinerario_id` → nome, quando a origem o traz.
     *
     * ⚠️ Vazio quando não vem: o nome fica `''` e quem mostra cai no código. **Não se inventa um
     * nome a partir do id** — `IT-BCN5PAR` é uma chave, não uma designação, e apresentá-la como nome
     * fazia o mapa parecer que o itinerário tem nome quando ninguém lho deu.
     */
    const nomeDoItinerario = new Map();
    for (const it of csv.itinerarios ?? []) {
        const id = it['id'] ?? '';
        const nome = it['nome'] ?? '';
        if (id !== '' && nome !== '')
            nomeDoItinerario.set(id, nome);
    }
    /**
     * Registar um itinerário e devolver o índice. **Uma só função para os três sítios** que o
     * fazem (a linha normal, a `partida` do dia de encaixe e o subcontratado) — antes eram três
     * blocos iguais, e o terceiro já usava `registar`, que não sabe de pares.
     */
    const registarItinerario = (id) => {
        if (id === '')
            return -1;
        const achado = iItinerario.get(id);
        if (achado !== undefined)
            return achado;
        const novo = itinerarios.length;
        itinerarios.push([id, nomeDoItinerario.get(id) ?? '']);
        iItinerario.set(id, novo);
        return novo;
    };
    // Tabelas de baixa cardinalidade, recolhidas na ordem em que aparecem. Não são
    // fixadas em código: a folha real tem `Oficina`, `Avariado` e `Sinistro`, mas
    // nada garante que não apareça um quarto valor, e fixá-los faria o mapa
    // esconder silenciosamente o que não conhecesse.
    const iEstadoViatura = new Map();
    const estadosViatura = [];
    const iMeio = new Map();
    const meios = [];
    const iCorrida = new Map();
    const corridas = [];
    // ⚠️ O pedido a que cada célula pertence — ver `CelulaCompacta.pedido`. Fica vazia no caminho
    //    dos CSV, que não trazem a coluna.
    const iPedido = new Map();
    const pedidos = [];
    const registar = (valor, indice, tabela) => {
        if (valor === '')
            return -1;
        const achado = indice.get(valor);
        if (achado !== undefined)
            return achado;
        const novo = tabela.length;
        tabela.push(valor);
        indice.set(valor, novo);
        return novo;
    };
    // ---- Os subcontratados, em SECÇÃO PRÓPRIA (D-002) ------------------------
    //
    // ⚠️ Escolha do cliente entre duas alternativas (2026-08-06): uma linha por
    // subcontratado no fim da grelha, ou uma secção própria. **Secção própria** — e a razão
    // é o invariante: **um subcontratado pode ter vários serviços no mesmo dia**, e deve
    // poder. Não é um objecto físico da GVA, é uma empresa que resolve; o índice único da
    // base só se aplica a `viatura_id`, e uma célula de grelha só sabe dizer «um» ou «nenhum».
    //
    // ⚠️ `iSub` mapeia o ID para a linha da SECÇÃO, não para um índice de viatura. São
    // espaços diferentes, e confundi-los punha um serviço subcontratado na linha de uma
    // viatura própria com o mesmo número de ordem.
    const subcontratados = (csv.subcontratados ?? []).map((sc) => ({
        id: sc['id'] ?? '',
        nome: sc['nome'] ?? '',
        base: -1,
        // ⚠️ **Só os dois valores do `CHECK` da migração 031 passam; tudo o resto é `''`.** Ver o
        //    contrato: `''` é «a origem não disse», e não `parceiro_local`. Um valor desconhecido
        //    tratado como local fazia o ecrã afirmar que a empresa cobra por km.
        tipo: tipoDoParceiro(sc['tipo']),
    }));
    const iSub = new Map();
    subcontratados.forEach((sc, i) => iSub.set(sc.id, i));
    const grelhaSubcontratados = subcontratados.map(() => Array.from({ length: nDias }, () => []));
    /** 🔴 As viagens sem executor nenhum (migração 089) — ver `DadosMapa.grelhaEspera`. */
    const grelhaEspera = Array.from({ length: nDias }, () => []);
    const grelha = csv.viaturas.map(() => new Array(nDias).fill(null));
    const movimentos = csv.viaturas.map(() => new Array(nDias).fill(null));
    /** 📗 6.8 — esparsas, ver a nota no contrato. */
    const notas = {};
    /** As horas da linha `partida`, por `viatura:dia`. Ver `DadosMapa.movimentosSaida`. */
    const movimentosSaida = {};
    /** As horas da linha `principal`, por `viatura:dia`. Ver `DadosMapa.movimentosChegada`. */
    const movimentosChegada = {};
    /**
     * Os dias-viatura por que a passagem das linhas `partida` já passou.
     *
     * ⚠️ Existe porque a mesma `partida` chega lá duas vezes quando tem tripulação dupla, e o
     * que se quer saber é «já fundi o par deste dia?» — pergunta que a presença da chave em
     * `movimentosChegada` **não** responde, porque a `principal` pode legitimamente não ter horas.
     */
    const diasComPartidaVistos = new Set();
    /** Para onde a viatura vai num dia em trânsito. Ver `DadosMapa.destinosDoTransito`. */
    const destinosDoTransito = {};
    /** Quem mexeu e quando. Ver `DadosMapa.alteracoes`. */
    const alteracoes = {};
    /**
     * Regista a alteração de uma linha, ficando com a MAIS RECENTE do quadrado.
     *
     * ⚠️⚠️ **Um dia de rotação tem DUAS linhas** (migração 027) e as duas podem ter sido
     * mexidas por pessoas diferentes. A célula é uma, logo há que escolher — e a escolha é a
     * **mais recente**, que é a que responde à pergunta «isto mudou desde que o li?».
     * 📌 Ficar com a primeira que aparecesse fazia a resposta depender da ordem da consulta,
     * que é a definição de um valor plausível no lugar do certo.
     *
     * ⚠️ Só entra quem TEM carimbo. Uma entrada por célula vazia enchia o JSON com 91 250
     * chaves para servir as poucas que foram modificadas — a mesma conta das `notas`.
     */
    const registarAlteracao = (vi, dia, a) => {
        const quando = (a['actualizado_em'] ?? '').trim();
        if (quando === '')
            return;
        const chave = `${vi}:${dia}`;
        const jaLa = alteracoes[chave];
        if (jaLa !== undefined && jaLa[0] >= quando)
            return;
        const quem = (a['actualizado_por'] ?? '').trim();
        alteracoes[chave] = [quando, quem === '' ? null : quem];
    };
    /** ⚠️ B02: as decisões do cartão da paragem, por `viatura:dia`. Ver `pernoitaDecidida`. */
    const pernoitaDecidida = {};
    /** A cor manual de uma viagem, por `referencia`. Ver `DadosMapa.coresCorrida`. */
    const coresCorrida = {};
    for (const a of csv.coresCorrida ?? []) {
        const ref = a['referencia'] ?? '';
        if (ref === '')
            continue;
        const indice = Number(a['indice_cor']);
        if (!Number.isInteger(indice) || indice < 0)
            continue;
        coresCorrida[ref] = indice;
    }
    // ---- ⚠️⚠️ AS DECISÕES DO CARTÃO DA PARAGEM (B02, migração 036) -----------------
    //
    //   Linhas de motorista com `viatura_contexto_id`: o planeador registou, sobre o intervalo
    //   desta viatura, que este condutor **fica no local** ou **vai a casa**.
    //
    // 📌 Entram por uma lista PRÓPRIA e não pelas `alocacoes`: no ciclo principal cairiam no
    //   `vi === undefined` (não têm `viatura_id`) e desapareciam — que é exactamente o defeito.
    for (const a of csv.decisoesDoMotorista ?? []) {
        const vi = iViatura.get(a['viatura_contexto_id'] ?? '');
        if (vi === undefined)
            continue;
        const dia = diaNaJanela(a['data'] ?? '', janela);
        if (dia < 0 || dia >= nDias)
            continue;
        const mi = iMotorista.get(a['motorista_id'] ?? '');
        if (mi === undefined)
            continue;
        pernoitaDecidida[`${String(vi)}:${String(dia)}`] = {
            motorista: mi,
            tipoDia: a['tipo_dia'] ?? '',
            localidade: iLocalidade.get(a['localidade'] ?? '') ?? -1,
        };
    }
    // ⚠️ AS LINHAS `partida` FICAM PARA O FIM, e a razão é de ordem: a célula do dia é
    // construída pela linha `principal`, e uma `partida` que chegasse primeiro não teria
    // onde escrever a sua identidade. Guardá-las e aplicá-las depois torna o resultado
    // **independente da ordem** em que a consulta as devolve — em vez de depender de um
    // `ORDER BY` que um dia alguém altera sem saber que isto existia.
    const partidas = [];
    for (const a of csv.alocacoes) {
        // ---- Subcontratado: vai para a SECÇÃO PRÓPRIA e não para a grelha ------
        //
        // ⚠️ Antes esta linha caía no `continue` abaixo — `iViatura.get('')` dá `undefined` —
        // e o serviço **desaparecia**. A reserva gravava correctamente na base e não aparecia
        // em lado nenhum, o que é pior do que aparecer mal: o planeador reservava um
        // subcontratado e o mapa continuava a mostrar o dia vazio. Foi o cliente a perguntar.
        const sci = iSub.get(a['subcontratado_id'] ?? '');
        if (sci !== undefined) {
            const diaSub = diaNaJanela(a['data'] ?? '', janela);
            if (diaSub >= 0 && diaSub < nDias) {
                const linhaSub = grelhaSubcontratados[sci];
                let bandSub = 0;
                if ((a['movimento_in'] ?? '') !== '')
                    bandSub |= BANDEIRA.MOVIMENTO_IN;
                if ((a['movimento_out'] ?? '') !== '')
                    bandSub |= BANDEIRA.MOVIMENTO_OUT;
                if (a['estado_registo'] === 'reserva')
                    bandSub |= BANDEIRA.RESERVA;
                if (a['marcador_duvida'] === 'sim')
                    bandSub |= BANDEIRA.DUVIDA;
                bandSub |= bandeiraDoLocalPresumido(a);
                const iiSub = registarItinerario(a['itinerario'] ?? '');
                // ⚠️ `push` e não atribuição: **vários serviços no mesmo dia são legítimos**, e é
                // exactamente por isso que isto não pode ser uma linha da grelha normal.
                // O motorista fica `-1`: o subcontratado traz o seu, e nós não o conhecemos.
                linhaSub?.[diaSub]?.push([
                    iCliente.get(a['cliente_id'] ?? '') ?? -1,
                    iLocalidade.get(a['localidade'] ?? '') ?? -1,
                    -1,
                    0,
                    bandSub,
                    iiSub,
                    -1,
                    -1,
                    registar(a['meio'] ?? '', iMeio, meios),
                    -1,
                    registar(a['referencia'] ?? '', iCorrida, corridas),
                    // Um subcontratado nunca tem linha `partida`: o `CHECK` da migração 027 exige
                    // viatura própria, porque ele já podia ter vários serviços no mesmo dia.
                    -1,
                    -1,
                    -1,
                    -1,
                    -1,
                    // 16 · o pedido — ver `CelulaCompacta.pedido`.
                    registar(a['pedido'] ?? '', iPedido, pedidos),
                ]);
            }
            continue;
        }
        // ---- 🔴 EM ESPERA: sem executor nenhum, vai para a LISTA PRÓPRIA (migração 089) ----
        //
        // ⚠️⚠️ **É AQUI QUE A LINHA DEIXAVA DE EXISTIR.** Uma viagem em espera tem os três
        //    executores a `NULL`, logo caía no `vi === undefined` quatro linhas abaixo e
        //    **desaparecia do mapa** — exactamente o defeito que fez reverter a peça E
        //    (`e7cae6d7`): *«uma escrita que não se vê é indistinguível de uma que não aconteceu»*.
        //    O `dados-mapa.ts` já a traz no payload desde 2026-09-15, e declarava por escrito que o
        //    domínio a deitava fora. É esse buraco que isto fecha.
        //
        // ⚠️ **A condição é o `tipo_dia` e não «não tem executor», e é deliberado.** `alocacao_executor`
        //    (089) diz que os dois são equivalentes — `em_espera` implica nenhum executor e
        //    vice-versa —, e destes dois o `tipo_dia` é o que a origem DECLARA. Inferir o estado da
        //    ausência de colunas é o que fez a linha só-de-motorista (D-087) ser lida como espera no
        //    `dados-mapa.ts`, e é a mesma disciplina de `LOCAL_INFERIDO`, `EM_TRANSITO` e `FIXADO`.
        if ((a['tipo_dia'] ?? '') === EM_ESPERA) {
            const diaEsp = diaNaJanela(a['data'] ?? '', janela);
            if (diaEsp < 0 || diaEsp >= nDias)
                continue;
            const doDia = grelhaEspera[diaEsp];
            if (doDia === undefined)
                continue;
            const refEsp = registar(a['referencia'] ?? '', iCorrida, corridas);
            const clienteEsp = iCliente.get(a['cliente_id'] ?? '') ?? -1;
            const itinEsp = registarItinerario(a['itinerario'] ?? '');
            const motEsp = iMotorista.get(a['motorista_id'] ?? '') ?? -1;
            // ⚠️⚠️ **A SEGUNDA LINHA DO MESMO SERVIÇO É O 2.º CONDUTOR, e não um segundo serviço.**
            //    A consulta do mapa devolve **uma linha por condutor** (`LEFT JOIN alocacao_condutor`),
            //    e 📗 *«tem de sobreviver TUDO, incluindo condutores»* — logo uma tripulação dupla em
            //    espera chega aqui duas vezes. Empilhá-la desenhava **duas barras** onde há uma
            //    viagem, e duas barras leem-se como dois serviços. É a mesma decisão que a grelha da
            //    frota toma no ramo `existente != null`.
            const jaLa = doDia.findIndex((c) => refEsp >= 0 ? c[10] === refEsp : c[0] === clienteEsp && c[5] === itinEsp);
            const existenteEsp = jaLa >= 0 ? doDia[jaLa] : undefined;
            if (existenteEsp !== undefined) {
                // ⚠️ A mesma leitura do `papel` que a frota faz: `''` é «a origem não declara» e
                //    acende a dupla, porque recusar por aí apagava as 95 células reais de `David/Cotas`.
                const papelEsp = a['papel'] ?? '';
                if (motEsp >= 0 &&
                    motEsp !== existenteEsp[2] &&
                    existenteEsp[6] < 0 &&
                    (papelEsp === '' || papelEsp === '2o_condutor')) {
                    doDia[jaLa] = [
                        existenteEsp[0],
                        existenteEsp[1],
                        existenteEsp[2],
                        existenteEsp[3],
                        existenteEsp[4] | BANDEIRA.TRIPULACAO_DUPLA,
                        existenteEsp[5],
                        motEsp,
                        existenteEsp[7],
                        existenteEsp[8],
                        existenteEsp[9],
                        existenteEsp[10],
                        existenteEsp[11],
                        existenteEsp[12],
                        existenteEsp[13],
                        existenteEsp[14],
                        existenteEsp[15],
                        existenteEsp[16],
                    ];
                }
                continue;
            }
            let bandEsp = 0;
            if ((a['movimento_in'] ?? '') !== '')
                bandEsp |= BANDEIRA.MOVIMENTO_IN;
            if ((a['movimento_out'] ?? '') !== '')
                bandEsp |= BANDEIRA.MOVIMENTO_OUT;
            if (a['estado_registo'] === 'reserva')
                bandEsp |= BANDEIRA.RESERVA;
            if (a['marcador_duvida'] === 'sim')
                bandEsp |= BANDEIRA.DUVIDA;
            bandEsp |= bandeiraDoLocalPresumido(a);
            doDia.push([
                clienteEsp,
                iLocalidade.get(a['localidade'] ?? '') ?? -1,
                motEsp,
                0,
                bandEsp,
                itinEsp,
                -1,
                // ⚠️ **7 · o estado da VIATURA fica a `-1`, e não é omissão: não há viatura.** É um
                //    facto sobre o autocarro, e `recusarOQueNaoCabe` (`espera.ts`) recusa pousar um dia
                //    que o tenha, precisamente para a avaria não vir parar a uma linha sem autocarro —
                //    a falha grave de 2026-08-07.
                -1,
                registar(a['meio'] ?? '', iMeio, meios),
                iLocalidade.get(a['local_embarque'] ?? '') ?? -1,
                refEsp,
                // ⚠️ 11-15 · não há viagem que parte: uma linha em espera é sempre `principal` e não
                //    tem dia de rotação. Desde 2026-09-29 o dia de rotação separa-se ao pôr em espera
                //    (`espera.ts`): a nossa linha sai `principal` na mesma instrução que lhe tira a
                //    viatura (o CHECK da 027 não deixa uma `partida` sem autocarro), e a outra viagem
                //    fica no autocarro, promovida a `principal` por `normalizarPapelDoDia`. O «trazer»
                //    recria a rotação — no autocarro, nunca aqui.
                -1,
                -1,
                -1,
                -1,
                -1,
                registar(a['pedido'] ?? '', iPedido, pedidos),
            ]);
            continue;
        }
        const vi = iViatura.get(a['viatura_id'] ?? '');
        if (vi === undefined)
            continue;
        const dia = diaNaJanela(a['data'] ?? '', janela);
        if (dia < 0 || dia >= nDias)
            continue;
        const linha = grelha[vi];
        if (linha === undefined)
            continue;
        // ---- A segunda viagem do dia de rotação (migração 027) -----------------
        //
        // ⚠️⚠️ TEM DE SER APANHADA AQUI, ANTES do `existente != null` abaixo. Esse ramo
        // trata uma segunda linha na mesma viatura-dia como **tripulação dupla** (as 95
        // células de `David/Cotas`), e engoliria a viagem que parte como se fosse um
        // segundo condutor: nada estoura, nada avisa, e a célula fica errada. Foi por
        // isto que a migração 027 não pôde ser publicada antes desta alteração.
        if ((a['papel_no_dia'] ?? 'principal') === 'partida') {
            partidas.push({ vi, dia, a });
            continue;
        }
        const existente = linha[dia];
        if (existente != null) {
            // Segunda alocação no mesmo dia e viatura = tripulação dupla (95 células
            // medidas na folha real, `David/Cotas` na folha original).
            //
            // A célula já existe e o resto dos seus campos é o mesmo — cliente,
            // itinerário e localidade vêm do itinerário, não do condutor. Só o
            // condutor é novo, e é isso que se acrescenta. Descartar esta linha
            // apagava metade da tripulação do mapa.
            const m2 = iMotorista.get(a['motorista_id'] ?? '') ?? -1;
            // ---- ⚠️⚠️ V23 · A TRIPULAÇÃO DUPLA ERA INFERIDA DA FORMA DOS DADOS ----------------
            //
            // 📗 Cliente, 2026-08-25 (pág. 66-68): *«Não sei porquê mas nesta viagem planeei e pôs
            //    uma tripulação dupla, sendo que estava definido só o MC.»*
            //
            // ⚠⚠ **A regra era «duas linhas com condutores diferentes»**, e mais nada — logo
            //    *qualquer* segunda linha (um resíduo, uma escrita parcial) se lia como **dois
            //    condutores ao mesmo tempo no mesmo autocarro**, que é um **regime legal diferente**
            //    (art. 8.º n.º 5, art. 4.º o). 📌 A coluna que o diz **já vinha na consulta**
            //    (`dados-mapa.ts`: `ac.papel`) e está nos CSV desde sempre — e o construtor nunca a
            //    leu. É a forma de defeito desta base de código: o servidor publica, o leitor não lê.
            //
            // ⚠️ **Não é um palpite que os dados a distinguem — está medido** (`reservar.ts`,
            //    2026-08-28, sonda `sonda-b26-condutor-por-cima.mjs`): **292** alocações com
            //    `2o_condutor` (a dupla legítima) contra **1** com duas linhas `papel='condutor'` — e
            //    essa uma é o resíduo do defeito corrigido nesse dia, que é exactamente o caso do
            //    cliente. Ler o papel converte esse resíduo num condutor só, e não num regime legal.
            //
            // ⚠️ **A ausência da coluna acende a dupla, como até aqui.** Um `papel` vazio significa
            //    «esta origem não o declara», e recusar por aí apagava as 95 células reais de
            //    `David/Cotas` em qualquer leitor que não o traga — a mesma disciplina do
            //    `papel_no_dia` dez linhas acima (`?? 'principal'`).
            const papelDaLinha = a['papel'] ?? '';
            const eSegundoCondutor = papelDaLinha === '' || papelDaLinha === '2o_condutor';
            if (m2 >= 0 && m2 !== existente[2] && existente[6] < 0 && eSegundoCondutor) {
                linha[dia] = [
                    existente[0],
                    existente[1],
                    existente[2],
                    existente[3],
                    existente[4] | BANDEIRA.TRIPULACAO_DUPLA,
                    existente[5],
                    m2,
                    existente[7],
                    existente[8],
                    existente[9],
                    existente[10],
                    // ⚠️ A viagem que parte PRESERVA-SE. Se a `partida` já tivesse sido aplicada,
                    // acrescentar um 2.º condutor apagava-a — e a célula perdia metade do dia.
                    existente[11],
                    existente[12],
                    existente[13],
                    // ⚠️ E o CONDUTOR da viagem que parte também se preserva, pela mesma razão: se a
                    //    linha `partida` já tinha sido aplicada, acrescentar um 2.º condutor
                    //    simultâneo apagava quem conduz a segunda viagem do dia.
                    existente[14],
                    existente[15],
                    // 16 · preserva-se o pedido: esta passagem só acrescenta o 2.º condutor.
                    existente[16],
                ];
            }
            continue;
        }
        const movIn = a['movimento_in'] ?? '';
        const movOut = a['movimento_out'] ?? '';
        let bandeiras = 0;
        if (movIn !== '')
            bandeiras |= BANDEIRA.MOVIMENTO_IN;
        if (movOut !== '')
            bandeiras |= BANDEIRA.MOVIMENTO_OUT;
        if (a['dia_rotacao'] === 'sim')
            bandeiras |= BANDEIRA.ROTACAO;
        if (a['estado_registo'] === 'reserva')
            bandeiras |= BANDEIRA.RESERVA;
        if (a['marcador_duvida'] === 'sim')
            bandeiras |= BANDEIRA.DUVIDA;
        // ⚠️ A localidade é NOSSA e não do plano. Ver `BANDEIRA.LOCAL_INFERIDO`.
        if (a['origem_localizacao'] === 'inferida')
            bandeiras |= BANDEIRA.LOCAL_INFERIDO;
        // ⚠️ E a presumida (migração 093) — ver `BANDEIRA.LOCAL_PRESUMIDO`.
        bandeiras |= bandeiraDoLocalPresumido(a);
        // ---- ⚠️⚠️ EM TRÂNSITO — 📗 «esses dias têm de aparecer … em trânsito» ----
        //
        //   `tipo_dia = 'vazio'` existe desde a migração 013 e era **seleccionado pelo SQL do mapa e
        //   nunca usado**. Passa a chegar à célula como bandeira, e o DESTINO ao mapa esparso.
        //
        // ⚠️ **Não se infere.** Um dia sem itinerário não é «em trânsito»: um dia parado também não
        //   tem itinerário, e chamar-lhe trânsito punha a viatura a andar quando ela está quieta. Só
        //   o que a origem **declara** conta — é a mesma disciplina de `LOCAL_INFERIDO`.
        // ⚠️ O alfinete (migração 029). Só o que a origem declara — como `LOCAL_INFERIDO`.
        if (a['fixado'] === 'sim' || a['fixado'] === 'true')
            bandeiras |= BANDEIRA.FIXADO;
        // ---- ⚠️⚠️ ESTE DIA É DE OUTRO PLANO (D-1) ------------------------------------
        //
        //   📗 Cliente, 2026-08-21: *«aparece a ferramenta como se fosse oficina e diz "noutro
        //   plano". Qual é a lógica disto?»*
        //
        //   📌 **Só o que a ORIGEM declara**, como o `LOCAL_INFERIDO`, o `EM_TRANSITO` e o
        //   `FIXADO`. Inferir «é de outro plano» de «não tem cliente» punha a bandeira em todas as
        //   paragens do mapa — e uma paragem é o oposto: é um dia livre.
        //
        // ⚠️ Os CSV não têm esta coluna e nunca a terão: sem base de dados o mapa é de todos os
        //   planos, logo nenhum dia é «de outro». A ausência lê-se como `''` e não acende nada.
        if (a['noutro_plano'] === 'sim')
            bandeiras |= BANDEIRA.NOUTRO_PLANO;
        if (a['tipo_dia'] === 'vazio') {
            bandeiras |= BANDEIRA.EM_TRANSITO;
            const dest = iLocalidade.get(a['destino_vazio'] ?? '');
            // ⚠️ Só quando se sabe: sem destino, a célula diz «em trânsito» e não inventa para onde.
            if (dest !== undefined)
                destinosDoTransito[`${vi}:${dia}`] = dest;
        }
        // ---- ⚠️⚠️ COMPROMETIDA E SEM TRABALHAR — §8.3, migração 072 -----------------
        //
        //   📌 **Só o que a ORIGEM declara**, como o `EM_TRANSITO`, o `LOCAL_INFERIDO`, o `FIXADO` e
        //   o `NOUTRO_PLANO`. Inferir «não trabalha» de «não tem itinerário» punha a bandeira em
        //   todas as paragens do mapa, que são o oposto: dias LIVRES.
        //
        // ⚠️ **Sem mapa esparso.** O dia não tem destino — a viatura não vai a sítio nenhum —, e
        //   `localidade_id` (o campo 1) já diz onde ela está. O `destino_vazio` da 013 é do dia
        //   `vazio` e não se reaproveita: a nota da coluna diz *«este é para onde vai»*.
        //
        // ⚠️ Os CSV não têm este valor (a folha real não o distingue), logo a ausência lê-se como
        //   `''` e não acende nada — o mesmo comportamento que o `NOUTRO_PLANO` tem lá.
        if (a['tipo_dia'] === PARADO_NO_SERVICO)
            bandeiras |= BANDEIRA.PARADO_NO_SERVICO;
        // ⚠️ A fronteira de um troço administrativo (migração 085) — ver `BANDEIRA.INICIO_DE_TROCO`.
        //   Só o que a origem declara, como as bandeiras acima.
        if (a['inicio_de_troco'] === 'sim')
            bandeiras |= BANDEIRA.INICIO_DE_TROCO;
        const ii = registarItinerario(a['itinerario'] ?? '');
        const papel = ESTADOS.indexOf((a['estado_visual'] ?? 'bom'));
        linha[dia] = [
            iCliente.get(a['cliente_id'] ?? '') ?? -1,
            iLocalidade.get(a['localidade'] ?? '') ?? -1,
            iMotorista.get(a['motorista_id'] ?? '') ?? -1,
            papel < 0 ? 0 : papel,
            bandeiras,
            ii,
            -1, // 2.º condutor: preenchido pela alocação seguinte, se houver
            registar(a['estado_viatura'] ?? '', iEstadoViatura, estadosViatura),
            registar(a['meio'] ?? '', iMeio, meios),
            iLocalidade.get(a['local_embarque'] ?? '') ?? -1,
            registar(a['referencia'] ?? '', iCorrida, corridas),
            // A viagem que parte entra na passagem própria das linhas `partida`, mais abaixo.
            -1,
            -1,
            -1,
            -1,
            -1,
            // ---- 16 · O PEDIDO, e é o que desambigua a referência (2026-08-31) ----------------
            //
            //   ⚠️⚠️ A referência liga *a mesma viagem em duas viaturas* de propósito (F-19/F-22), logo
            //   **não** responde a «esta célula é deste pedido?». Medido no `staging`: **24 de 275
            //   referências pertencem a mais do que um pedido**, em 507 dias.
            //
            // ⚠️ `-1` no caminho dos CSV, que não têm a coluna — e quem lê cai na referência.
            registar(a['pedido'] ?? '', iPedido, pedidos),
        ];
        const linhaMov = movimentos[vi];
        if (linhaMov !== undefined && (movIn !== '' || movOut !== '')) {
            linhaMov[dia] = [movIn, movOut];
        }
        // ⚠️ Só entra quem TEM nota. Uma entrada `''` por célula vazia enchia o JSON com
        // 91 250 chaves para servir as poucas dezenas que têm texto.
        const nota = (a['notas'] ?? '').trim();
        if (nota !== '')
            notas[`${vi}:${dia}`] = nota;
        registarAlteracao(vi, dia, a);
    }
    // ==========================================================================
    // A VIAGEM QUE PARTE — a linha `partida` da migração 027
    // ==========================================================================
    //
    // 📗 Cliente: *«se eu planear uma viagem que coincide no dia de arranque de outra o
    //    sistema já lança aquele campo a preto de rotação?»*
    //
    // A identidade da segunda viagem entra nos campos 11 a 13. **Não substitui nada:** os
    // campos 0, 5 e 10 continuam a ser da viagem que CHEGA, que é o que a caixa preta e a
    // banda 1 mostram hoje. É o painel, o cartão e o «Ver no mapa» que ganham a segunda
    // metade da célula.
    //
    // ⚠️ As NOTAS ficam na linha `principal`, de propósito: o lápis escreve na célula, e a
    // célula é uma. Duas notas para o mesmo quadrado obrigariam a inventar uma política de
    // fusão que ninguém pediu — `nota-alocacao.ts` escreve sempre no `principal`.
    for (const { vi, dia, a } of partidas) {
        const linha = grelha[vi];
        if (linha === undefined)
            continue;
        // ⚠️ A linha `partida` é a OUTRA viagem do mesmo quadrado, e pode ter sido mexida
        //    depois da `principal`. `registarAlteracao` fica com a mais recente das duas —
        //    ao contrário das NOTAS, que vivem sempre na `principal` por decisão do lápis.
        registarAlteracao(vi, dia, a);
        const movOut = a['movimento_out'] ?? '';
        // ⚠️ **O `in` DA LINHA `partida` ERA DEITADO FORA, e passou a existir hoje.** Desde
        //    2026-08-14 a reserva escreve as horas do pedido, e a `recolha` da viagem que PARTE
        //    cai nesta linha — logo sem isto o planeador marcava a hora de chegada do grupo novo
        //    e ela **não aparecia em lado nenhum**. Medido na folha real: os primeiros dias de
        //    serviço levam `in` **513** vezes contra **5** `out`, e os dias de rotação levam `in`
        //    **90** vezes contra **3** — um dia de rotação comporta-se como o PRIMEIRO dia da
        //    viagem que parte, que é exactamente o que F-25 prevê.
        const movInSaida = a['movimento_in'] ?? '';
        const cs = iCliente.get(a['cliente_id'] ?? '') ?? -1;
        const is = registarItinerario(a['itinerario'] ?? '');
        const rs = registar(a['referencia'] ?? '', iCorrida, corridas);
        // O condutor DESTA viagem — o campo 14, que não é o 6 (ver o tipo).
        const mS = iMotorista.get(a['motorista_id'] ?? '') ?? -1;
        const c = linha[dia];
        if (c == null) {
            // ⚠️ Uma `partida` sem `principal` no mesmo dia é possível no esquema — o que não
            // se pode fazer é deixá-la cair. Um serviço que desaparece do mapa é o defeito do
            // subcontratado outra vez: a base aceita a escrita e o planeador vê o dia vazio.
            // Sozinha, ela É a célula do dia, com a identidade nos campos normais.
            let band = movOut === '' ? 0 : BANDEIRA.MOVIMENTO_OUT;
            if (movInSaida !== '')
                band |= BANDEIRA.MOVIMENTO_IN;
            if (a['estado_registo'] === 'reserva')
                band |= BANDEIRA.RESERVA;
            if (a['marcador_duvida'] === 'sim')
                band |= BANDEIRA.DUVIDA;
            // ⚠️ A fronteira de um troço (migração 085) pode ser desta linha `partida` — ver
            //   `BANDEIRA.INICIO_DE_TROCO`.
            if (a['inicio_de_troco'] === 'sim')
                band |= BANDEIRA.INICIO_DE_TROCO;
            // ⚠️ Sozinha, a cidade da célula é a DELA — logo a marca (093) também.
            band |= bandeiraDoLocalPresumido(a);
            linha[dia] = [
                cs,
                iLocalidade.get(a['localidade'] ?? '') ?? -1,
                // ⚠️ Sozinha, ela É a viagem do dia — logo o condutor dela é o condutor da célula
                //    (campo 2) e não o «da que parte» (campo 14). Pôr no 14 deixava a banda 3 vazia
                //    num dia que tem serviço e condutor.
                mS,
                0,
                band,
                is,
                -1,
                -1,
                registar(a['meio'] ?? '', iMeio, meios),
                -1,
                rs,
                -1,
                -1,
                -1,
                -1,
                -1,
                // 16 · o pedido desta linha `partida` — ver `CelulaCompacta.pedido`.
                registar(a['pedido'] ?? '', iPedido, pedidos),
            ];
        }
        else {
            // ⚠️ AS HORAS DAS DUAS VIAGENS FICAM EM SÍTIOS DIFERENTES, e é o pedido do cliente:
            //   *«a hora de saída é a hora de saída de uma viagem e a hora de entrada é a hora da
            //   chegada da outra»*. As bandeiras dizem que o dia TEM chegada e partida; quem diz
            //   **de quem é cada uma** é `movimentos` (a linha que chega) contra `movimentosSaida`
            //   (a que parte). Uma bandeira não tem onde guardar a autoria.
            //
            // ✅ **D-084 FECHADO a 2026-08-14, decidido pelo cliente e medido antes:** o `out` é da
            //   viagem que **ACABA** (a linha `principal`) e o `in` da que **PARTE** (esta). A
            //   migração 027 tinha gravado o contrário e a **028** trocou-os — verificado na base:
            //   `in` 692 nesta linha / 0 na outra, e o `out` o inverso.
            //
            // ⚠️⚠️ **E A TRIPULAÇÃO DUPLA DESTA LINHA (2026-08-14).** A consulta do mapa devolve **uma
            //   linha por condutor**, logo uma `partida` com dois condutores chega aqui **duas vezes** —
            //   e a versão anterior escrevia sempre no campo 14, com o segundo a **sobrescrever** o
            //   primeiro: um condutor desaparecia sem erro nenhum. O segundo vai agora ao campo 15.
            const jaTinha = c[14];
            const segundoDaPartida = jaTinha >= 0 && mS >= 0 && mS !== jaTinha;
            linha[dia] = [
                c[0],
                c[1],
                c[2],
                c[3],
                (movOut === '' ? c[4] : c[4] | BANDEIRA.MOVIMENTO_OUT) |
                    (movInSaida === '' ? 0 : BANDEIRA.MOVIMENTO_IN) |
                    // ⚠️ A fronteira de um troço (migração 085) pode ser desta linha `partida` mesmo
                    //   quando o dia já tinha `principal` — ver `BANDEIRA.INICIO_DE_TROCO`.
                    (a['inicio_de_troco'] === 'sim' ? BANDEIRA.INICIO_DE_TROCO : 0),
                c[5],
                c[6],
                c[7],
                c[8],
                c[9],
                c[10],
                cs,
                is,
                rs,
                // ⚠️ O primeiro condutor PRESERVA-SE: `mS` só entra no 14 se ele ainda estiver vazio.
                segundoDaPartida ? jaTinha : mS >= 0 ? mS : jaTinha,
                segundoDaPartida ? mS : c[15],
                // 16 · preserva-se o pedido: esta passagem só mexe nos condutores da partida.
                c[16],
            ];
        }
        // ⚠️ AS HORAS DESTA LINHA, guardadas à parte e com a autoria preservada.
        if (movInSaida !== '' || movOut !== '') {
            movimentosSaida[`${vi}:${dia}`] = [movInSaida, movOut];
        }
        const linhaMov = movimentos[vi];
        // ---- ⚠️⚠️ AS HORAS DA `principal`, GUARDADAS ANTES DE SEREM FUNDIDAS (2026-09-07) ----
        //
        //   O par do dia está prestes a receber as horas DESTA linha por cima. Depois disso já não
        //   há como saber o que a `principal` tinha — e era isso que fazia o painel escrever a hora
        //   da viagem que PARTE também na que CHEGA. Ver `DadosMapa.movimentosChegada`.
        //
        // ⚠️ **A ausência de chave é a resposta certa quando a `principal` não tem hora nenhuma** —
        //   é o caso real da viatura 297 a 2026-08-29. Escrever `['','']` obrigava cada leitor a
        //   distinguir «existe e está vazio» de «não existe», que são a mesma coisa e dão dois ramos.
        //
        // ⚠️⚠️ **Só a PRIMEIRA passagem por este dia olha, e o guarda é um CONJUNTO e não a chave
        //   já escrita.** Uma `partida` com tripulação dupla chega aqui duas vezes (ver o campo 15,
        //   acima). Se a `principal` não tivesse horas, a primeira passagem não escreve nada — e à
        //   segunda o par do dia JÁ traz as horas da `partida`, logo um guarda por «a chave ainda
        //   não existe» gravava-as como sendo da `principal`. **O defeito outra vez, pela porta do
        //   segundo condutor.**
        const chaveDoDia = `${vi}:${dia}`;
        if (!diasComPartidaVistos.has(chaveDoDia)) {
            diasComPartidaVistos.add(chaveDoDia);
            const parDaPrincipal = linhaMov?.[dia];
            if (parDaPrincipal != null)
                movimentosChegada[chaveDoDia] = parDaPrincipal;
        }
        // ⚠️⚠️ **O `in` DESTA LINHA ENTRA NO PAR DO DIA — e sem isto o tempo de rotação morria.**
        //   Desde a decisão de D-084 (2026-08-14) o `in` do dia de rotação vive na linha `partida`
        //   e o `out` na `principal`: as duas horas do dia ficam em **linhas diferentes**.
        //   `tempoRotacaoMinutos` calcula a margem a partir do par do DIA — logo se o par só
        //   trouxesse a hora da linha `principal`, a margem passava a «não calculável» em todos os
        //   dias de rotação. 📌 O par continua a ser «as duas horas do dia»; quem precisa de saber
        //   **de quem** é cada uma usa `movimentosSaida`.
        if (linhaMov !== undefined && movInSaida !== '') {
            linhaMov[dia] = [movInSaida, linhaMov[dia]?.[1] ?? ''];
        }
        if (linhaMov !== undefined && movOut !== '') {
            // ⚠️ **Mantém-se por compatibilidade e está declarado:** `movimentos` é lido por seis
            //   sítios que mostram «as horas do dia», e num dia de rotação o par junto continua a
            //   ser o que a célula desenha. Quem precisa de saber **de quem** é cada hora usa
            //   `movimentosSaida`. Mudar o par aqui alterava o que todos eles mostram.
            linhaMov[dia] = [linhaMov[dia]?.[0] ?? '', movOut];
        }
    }
    // Segunda passagem: fronteiras da corrida e VALIDAÇÃO da rotação.
    //
    // A fronteira agrupa por CORRIDA (a viagem) e não pela assinatura de rota: duas
    // viagens distintas e seguidas com a mesma origem/destino são duas barras, não
    // uma. Onde não há corrida identificada, cai-se na assinatura, que é o melhor
    // que existe.
    for (const linha of grelha) {
        for (let d = 0; d < nDias; d++) {
            const c = linha[d];
            if (c == null)
                continue;
            const anterior = d > 0 ? (linha[d - 1] ?? null) : null;
            const seguinte = d + 1 < nDias ? (linha[d + 1] ?? null) : null;
            const chave = (x) => (x[10] >= 0 ? x[10] : x[5]);
            const mesmaCorrida = (x) => x !== null &&
                x[7] < 0 &&
                c[7] < 0 &&
                chave(x) === chave(c) &&
                // ⚠️ SEM CORRIDA NEM ROTA, a chave é `-1` — e `-1 === -1` para tudo o que não
                // tem identidade. Um dia PARADO na base e uma RESERVA sem itinerário caíam na
                // mesma barra, e a barra de «na base» — viatura parada, alojamento zero
                // (D-083) — vestia a textura de reserva, que significa viatura COM serviço.
                // Duas leituras opostas na mesma barra.
                //
                // Onde HÁ identidade (`chave >= 0`) o estado de registo **não** parte a barra:
                // «parte deste itinerário não está confirmada» é o que o planeador precisa de
                // ver numa barra só, e isso é deliberado. Só onde não há identidade nenhuma se
                // exige que o estado de registo coincida.
                //
                // Encontrado a comparar o mapa da base com o mapa dos CSV célula a célula,
                // depois de uma reserva sem itinerário aterrar ao lado de um dia parado.
                (chave(c) >= 0 || (x[4] & BANDEIRA.RESERVA) === (c[4] & BANDEIRA.RESERVA));
            /**
             * ⚠️⚠️ **A VIAGEM QUE PARTE NUM DIA DE ROTAÇÃO JÁ COMEÇOU — e o `PRIMEIRO_DIA` caía um dia
             * TARDE em 864 de 865 casos.**
             *
             * 📗 Cliente, 2026-08-12: *«a hora de chegada do grupo é dia 10 às 11, mas na realidade foi
             * dia 9 às 5 da manhã»*, e *«diz que a hora de chegada está por definir, mas o grupo já
             * chegou no dia anterior às 17:30»*.
             *
             * 📌 **A causa:** estas bandeiras eram calculadas só pela cadeia da linha `principal` — e num
             * dia de rotação a `principal` é a viagem que **acaba** (medido: 1 029 de 1 029 têm a
             * referência do dia anterior). A viagem que **parte** vive no campo 13 (`corridaSaida`, da
             * migração 027) e não entrava na comparação, logo o dia seguinte parecia ser o primeiro dela.
             *
             * ⚠️ **Consequência nos detalhes, que é o que o cliente viu:** «hora de chegada do grupo» é
             * lida do movimento **desse** dia. No dia errado, ou dá a hora errada, ou dá «POR DEFINIR» —
             * porque a chegada aconteceu no dia de rotação, e é lá que a hora está.
             *
             * ⚠️ **Medido antes de corrigir: 864 de 865.** Não era um caso isolado: era sistemático.
             */
            const comecouNaRotacao = (x) => x !== null && x[13] >= 0 && x[13] === chave(c);
            let b = c[4];
            if (!mesmaCorrida(anterior) && !comecouNaRotacao(anterior))
                b |= BANDEIRA.PRIMEIRO_DIA;
            if (!mesmaCorrida(seguinte))
                b |= BANDEIRA.ULTIMO_DIA;
            // ---- a rotação é DERIVADA, não aceite ------------------------------
            //
            // Rotação = chega um itinerário E parte outro, no mesmo dia. Foi o cliente
            // que a definiu assim: «a hora de chegada de um itinerário e a partida de
            // outro, para o motorista se orientar».
            //
            // Logo exige duas coisas verificáveis:
            //   · uma CHEGADA — o dia anterior é da mesma corrida, ou seja há viagem a
            //     terminar aqui;
            //   · uma PARTIDA — o dia seguinte tem serviço, ou seja há viagem a começar.
            //
            // Um dia negro seguido de dia livre não é rotação nenhuma. Medido: 14,3 %
            // dos dias marcados estavam nessa situação.
            if ((b & BANDEIRA.ROTACAO) !== 0) {
                const temChegada = mesmaCorrida(anterior) || (b & BANDEIRA.MOVIMENTO_IN) !== 0;
                // ⚠️ A PROVA DA PARTIDA MELHOROU com a migração 027, e a ordem importa: uma linha
                // `partida` **é** a viagem que parte, registada, e vale mais do que a inferência
                // «o dia seguinte tem serviço». Sem isto, uma viagem de UM SÓ DIA que arranca num
                // dia de rotação não tinha dia seguinte e a rotação era marcada como suspeita —
                // com a prova da partida na própria célula.
                // ⚠️⚠️ **UM DIA PARADO NÃO É UMA VIAGEM A PARTIR — e isto tinha MATADO a guarda de D-080.**
                //
                // 📗 Cliente, 2026-08-12, sobre uma célula preta seguida de «na base»: *«tem este caso que
                //    faz zero sentido»*. **Tem razão.**
                //
                // 📌 **Medido antes de corrigir:** 1 014 células desenhadas como rotação, **150 com o dia
                //    seguinte sem serviço nenhum**, e — o pior — **ZERO** células com `ROTACAO_SUSPEITA`.
                //    O `!` que D-080 introduziu para as 14,3 % de marcações erradas **nunca aparecia**.
                //
                // ⚠️ **A causa é a paragem inferida, pela quinta vez em cadeia:** ela dá célula a **todos**
                //    os dias seguintes, logo `seguinte !== null` passou a ser sempre verdade e a condição
                //    deixou de discriminar. É a família «contar células quando o produto conta SERVIÇO» —
                //    a mesma que já apanhei duas vezes no `verificar-rearranjo.mjs` e uma no impacto.
                //
                // 📌 A prova de que há viagem a partir é uma das duas: a linha `partida` na própria célula
                //    (campo 13, registada e indiscutível), **ou** um dia seguinte com **serviço** — cliente,
                //    itinerário ou corrida. Uma paragem não tem nenhum dos três.
                const seguinteTemServico = seguinte !== null &&
                    seguinte[7] < 0 &&
                    (seguinte[0] >= 0 || seguinte[5] >= 0 || seguinte[10] >= 0);
                const temPartida = c[13] >= 0 || seguinteTemServico;
                if (!(temChegada && temPartida)) {
                    b &= ~BANDEIRA.ROTACAO;
                    b |= BANDEIRA.ROTACAO_SUSPEITA;
                }
            }
            linha[d] = [
                c[0], c[1], c[2], c[3], b, c[5], c[6], c[7], c[8], c[9], c[10], c[11], c[12], c[13], c[14],
                // 16 · preserva-se o pedido: esta passagem só recalcula as bandeiras.
                c[15], c[16],
            ];
        }
    }
    // O índice da base, calculado uma vez: é o que distingue «esteve em casa» de «ficou
    // fora», e é lido dos dados (P-34) em vez de escrito no código.
    const localidadeBase = csv.localidades.findIndex((l) => l['e_base'] === 'sim');
    // ==========================================================================
    // ⚠️⚠️ A PARAGEM QUE NINGUÉM ESCREVEU — inferida entre dois serviços
    // ==========================================================================
    //
    // 📗 Cliente, 2026-08-07, sobre a consulta das paradas: *«continua a não bater certo o
    //    ver no mapa a partir do dia 15»*.
    //
    //    Medido na viatura 108: acaba um serviço em **Paris a 14 de Junho** e o seguinte só
    //    começa em **Lisboa a 7 de Julho**. Entre os dois há **22 dias sem linha nenhuma** —
    //    a consulta dizia «parada 22 dias em Paris» e o mapa mostrava a linha **em branco**.
    //    Os dois estavam certos e discordavam, que é o pior resultado possível: quem olha
    //    não sabe em qual acreditar.
    //
    // ⚠️ **É D-081 por acabar.** *«Todo o dia tem local»* ficou feito para os dias que TÊM
    //    linha; os que não têm continuaram invisíveis. E são 17 877 dias-viatura na base.
    //
    // ⚠️ **NÃO SE INFERE TUDO — só o que é impossível ser outra coisa.** Por convenção
    //    medida (P-34/D-083) o branco «significa» base: das 240 células com local em dias
    //    sem viagem, **nenhuma** é em Portugal. Essa convenção vale quando o serviço anterior
    //    acaba na base e o seguinte parte dela. **Não vale** quando ele acaba em Paris e o
    //    seguinte parte de Lisboa: a viatura não foi a Serzedelo e voltou sem que ninguém
    //    escrevesse nada. Só nesse caso é que se desenha — e desenha-se com
    //    `LOCAL_INFERIDO`, para o ecrã poder dizer que o local é **deduzido e não lido**.
    //
    // 📌 O que isto NÃO faz: não escreve na base. É uma leitura do plano, feita no mesmo
    //    sítio pelos dois lados (SQL e CSV), logo o `comparar.mjs` continua a comparar
    //    coisas equivalentes.
    for (let v = 0; v < grelha.length; v++) {
        const linha = grelha[v];
        if (linha === undefined)
            continue;
        let anterior = -1;
        for (let d = 0; d < nDias; d++) {
            if (linha[d] == null)
                continue;
            if (anterior >= 0 && d - anterior > 1) {
                const antes = linha[anterior];
                const depois = linha[d];
                const ondeAcabou = antes?.[1] ?? -1;
                const ondeRecomeca = depois?.[1] ?? -1;
                // ⚠️ Se acabou na base **e** recomeça na base, o branco é a convenção e não uma
                //    lacuna: a viatura esteve em casa e isso custa zero (D-060). Inferir aqui
                //    encheria o mapa de barras que não dizem nada.
                const foiParaCasa = ondeAcabou === localidadeBase &&
                    (ondeRecomeca === localidadeBase || ondeRecomeca < 0);
                if (!foiParaCasa && ondeAcabou >= 0) {
                    for (let x = anterior + 1; x < d; x++) {
                        // cliente, localidade, motorista, estado, bandeiras, itinerário, motorista2,
                        // estadoViatura, meio, localEmbarque, corrida
                        linha[x] = [
                            -1,
                            ondeAcabou,
                            -1,
                            0,
                            BANDEIRA.LOCAL_INFERIDO,
                            -1,
                            -1,
                            -1,
                            -1,
                            -1,
                            -1,
                            // Uma paragem inferida não tem viagem nenhuma — muito menos duas, nem
                            // condutor de uma segunda viagem.
                            -1,
                            -1,
                            -1,
                            -1,
                            -1,
                            // 16 · uma paragem INFERIDA não é de pedido nenhum: `-1` é a resposta certa.
                            -1,
                        ];
                    }
                }
            }
            // 🔴 2026-10-27 · **A CASCA RECEBE O LOCAL DO DIA ANTERIOR** — ver `cascaSemLocal`.
            //    DEPOIS do buraco e ANTES de passar a ser o «anterior»: o buraco antes dela já foi
            //    decidido com ela como «recomeça em sítio desconhecido» (que é o que ela é), e o buraco
            //    depois dela precisa de ler um sítio, senão também fica por preencher — era o caso
            //    #6856 (V0011: 04-06/11 cascas, 07-12/11 em branco, com o serviço a acabar em BCN).
            //    ⚠️ Só se escreve a localidade e a bandeira: o resto é o que a base diz.
            if (anterior >= 0 && cascaSemLocal(linha[d])) {
                const ondeEstava = linha[anterior]?.[1] ?? -1;
                if (ondeEstava >= 0)
                    linha[d] = preencherCascaSemLocal(linha[d], ondeEstava);
            }
            anterior = d;
        }
        // ⚠️⚠️ **E DEPOIS DO ÚLTIMO SERVIÇO TAMBÉM — que é o caso que só existe a sério em
        //    PRODUÇÃO.**
        //
        //    📗 Cliente, 2026-08-08: *«quando isto for para funcionar a sério o futuro vai estar
        //    todo vazio… como saber se está na base ou parada fora?»*.
        //
        //    O ciclo acima só preenche buracos **entre dois serviços**. Depois do último não há
        //    «seguinte», logo não havia buraco nenhum a preencher — e nos dados históricos isso
        //    não se notava, porque a folha tem serviços até ao fim do ano. Num plano vivo, tudo
        //    o que está para lá do último serviço fica branco, **e o branco já significa outra
        //    coisa**: por convenção medida (P-34/D-083) lê-se «na base», quando na verdade quer
        //    dizer «ainda ninguém decidiu».
        //
        // 📌 **A REGRA VEM DA ESCALA REAL, e não de uma suposição minha**
        //    (`tools/escala-real/confrontar.mjs`, 1 036 intervalos entre serviços):
        //      · **52,4 %** — a viatura fica **exactamente onde acabou**. É de longe o mais comum.
        //      · **26,4 %** — acabou fora e recomeça noutro sítio, também fora.
        //      · **5,8 %** — acabou fora e recomeçou em Portugal. **Voltar a casa é a excepção.**
        //    E mesmo em intervalos de 15+ dias, só **25,7 %** ficam no mesmo sítio e apenas
        //    **50 %** recomeçam em PT — não há nenhum limiar a partir do qual «volta à base».
        //
        // ⚠️ **Voltar à base não é um estado, é um SERVIÇO**: alguém tem de conduzir o autocarro
        //    até Serzedelo, e isso gasta um motorista, quilómetros e possivelmente uma pernoita.
        //    Enquanto essa deslocação não estiver no plano, a viatura **está onde ficou**.
        //
        // 📌 Custa UMA barra por viatura e não N células desenhadas: dias parados seguidos sem
        //    identidade juntam-se na mesma barra (ver a segunda passagem), logo o orçamento de
        //    desempenho do risco R1 não mexe.
        if (anterior >= 0) {
            const ultimo = linha[anterior];
            const ondeFicou = ultimo?.[1] ?? -1;
            // ⚠️ Se acabou **na base**, o branco é a convenção medida e não uma lacuna — a mesma
            //    guarda do ciclo acima. Desenhar aqui enchia o resto do ano de barras que dizem
            //    «em casa», que é precisamente o que o branco já diz.
            if (ondeFicou >= 0 && ondeFicou !== localidadeBase) {
                for (let x = anterior + 1; x < nDias; x++) {
                    if (linha[x] != null)
                        continue;
                    linha[x] = [
                        -1, ondeFicou, -1, 0, BANDEIRA.LOCAL_INFERIDO, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1,
                        // ⚠️ E o 16 também `-1`: uma paragem inferida não pertence a pedido nenhum.
                        -1, -1,
                    ];
                }
            }
        }
    }
    return {
        ano,
        inicio: janela.inicio,
        nDias,
        fimDeSemana,
        mesPorDia,
        diaDoMes,
        viaturas: csv.viaturas.map((v) => ({
            id: v['id'] ?? '',
            numero: v['numero_interno'] ?? '',
            matricula: v['matricula'] ?? '',
            lotacao: v['lotacao'] ?? '',
            // `A/C|WC|Wi-Fi` no CSV. Vazio dá lista vazia e não `['']`, que casaria com nada
            // e daria um requisito indeterminado sem razão.
            caracteristicas: (v['caracteristicas'] ?? '').split('|').filter((c) => c !== ''),
            // ⚠️⚠️ **O ANO da matrícula, e só o ano (038).** 📌 A data completa fica na base; a
            //    regra «máximo N anos» usa o ano, e o mapa viaja para o navegador em cada
            //    carregamento. ⚠️ Só quando é um ano plausível: um campo vazio dá `undefined` e a
            //    regra da idade **não exclui**, que é a resposta certa para «não se sabe».
            ...(() => {
                const bruto = String(v['data_matricula'] ?? '').slice(0, 4);
                const ano = Number(bruto);
                return Number.isInteger(ano) && ano > 1900 && ano < 2200 ? { anoMatricula: ano } : {};
            })(),
            motoristaPreferencial: iMotorista.get(v['motorista_preferencial'] ?? '') ?? -1,
            clienteDecoracao: iCliente.get(v['cliente_decoracao'] ?? '') ?? -1,
            plano: v['plano'] ?? '',
            // ⚠️ Só quando é `true`: um campo `false` em 250 viaturas é ruído no payload, e o
            //    leitor compara com `=== true`. Mesma convenção do `fixado` na célula.
            ...(v['bloqueada'] === 'sim' || v['bloqueada'] === 'true' ? { bloqueada: true } : {}),
            // ⚠️⚠️ **A AUSÊNCIA DA COLUNA LÊ-SE COMO ACTIVA, e é deliberado.** Os CSV de
            //    `data/sintetico` não têm `activo` nenhum — o campo chega vazio, e a resposta certa
            //    para «não sei» aqui é **activa**: desfocar 250 linhas porque a origem não traz a
            //    coluna era o mapa a afirmar que a frota inteira saiu de serviço. 📌 Por isso o SQL
            //    envia a marca já normalizada (`'sim'`/`''`), como o `bloqueada` acima, em vez de um
            //    `activo` que teria de ser negado por cada leitor.
            ...(v['inactiva'] === 'sim' || v['inactiva'] === 'true' ? { inactiva: true } : {}),
            // ⚠️ **Só quando é um número > 0.** Uma ficha vazia dá `undefined` e a viatura fica no custo
            //    médio — não se inventa consumo, e um `0` arrastava a média da frota para baixo.
            ...(() => {
                const c = Number(v['consumo_l_100km'] ?? '');
                return Number.isFinite(c) && c > 0 ? { consumoL100km: c } : {};
            })(),
        })),
        // ⚠️ `c['cor']` vem vazio quando a origem não a tem (CSV antigos, ou a coluna
        //    ainda sem valor) — `corDoCliente` lê `''` como «sem escolha».
        clientes: csv.clientes.map((c) => [c['sigla'] ?? '', c['nome'] ?? '', c['cor'] ?? '']),
        // ---- ⚠️⚠️ O DIMINUTIVO PODE NÃO EXISTIR, E A ESTRELA FICAVA SOZINHA -------------
        //
        //   📗 Cliente, 2026-08-25, a olhar para a coluna das viaturas: *«o condutor preferencial
        //   não aparece no mapa»*. 📌 **Medido: dos 75 motoristas reais da GVA, 75 estão sem
        //   `diminutivo`** — o cadastro tem `N.º Mec` e `Motorista`, e mais nada. A viatura tinha
        //   condutor preferencial, o índice resolvia, e o nome saía **vazio**: o mapa desenhava a
        //   estrela ★ sem uma letra ao lado.
        //
        // ⚠️ **A alternativa é o nome completo e não um diminutivo inventado.** Abreviar um nome
        //   por regra («primeiro + último») produz duas pessoas com o mesmo rótulo no dia em que a
        //   frota tiver dois «Silva», e um rótulo que identifica a pessoa errada é pior do que um
        //   comprido. O `title` do elemento e o CSS resolvem a largura; a identidade não se adivinha.
        //
        // 📌 **Continua a preferir o diminutivo quando ele existe** — os dados sintéticos trazem-no
        //   e as 28 provas do mapa não mudam. O que mudou é deixar de haver um caso em que o mapa
        //   mostra um sinal sem conteúdo.
        motoristasIds: csv.motoristas.map((m) => m['id'] ?? ''),
        // ⚠️ Mesma convenção do `inactiva` da viatura: a marca vem já normalizada e a **ausência
        //    da coluna lê-se como activo**. `flatMap` e não `filter`+`map` para não construir a
        //    lista intermédia — são 150 entradas por carregamento do mapa.
        motoristasInactivos: csv.motoristas.flatMap((m, i) => m['inactivo'] === 'sim' || m['inactivo'] === 'true' ? [i] : []),
        motoristas: csv.motoristas.map((m) => {
            const curto = (m['diminutivo'] ?? '').trim();
            return curto !== '' ? curto : (m['nome_completo'] ?? '').trim();
        }),
        localidades: csv.localidades.map((l) => [l['codigo_interno'] ?? '', l['nome'] ?? '']),
        // `-1` se os dados não declararem base. Não se adivinha pelo nome nem se fixa
        // o código: quem sabe qual é a base são os dados.
        localidadeBase,
        itinerarios,
        estados: ESTADOS,
        estadosViatura,
        meios,
        corridas,
        pedidos,
        grelha,
        subcontratados: subcontratados.map((sc) => ({
            ...sc,
            // ⚠️ A base resolve-se aqui e não acima: o índice de localidades só existe depois de
            // a lista ter sido construída, e resolvê-la antes dava sempre `-1`.
            base: iLocalidade.get((csv.subcontratados ?? []).find((x) => x['id'] === sc.id)?.['localidade_base'] ?? '') ?? -1,
        })),
        grelhaSubcontratados,
        grelhaEspera,
        movimentos,
        movimentosSaida,
        movimentosChegada,
        destinosDoTransito,
        coresCorrida,
        pernoitaDecidida,
        notas,
        alteracoes,
    };
}
/**
 * 🔴 2026-10-27 · **Uma CASCA: a linha existe e não diz nada sobre onde a viatura está.**
 *
 * 📗 Cliente, caso #6856 (`dev`): *«os dias onde estava a viagem ficaram a cinza — nem parado nem
 *    na base»*. Medido: as limpezas do trânsito (`limparTransitoOrfao`, `limparTransitoDaReserva`)
 *    transformam um dia de estrada em `tipo_dia = 'servico'` **sem localidade** — de propósito, e
 *    com a razão escrita lá: a localidade da estrada era o DESTINO e passa a ser falsa no instante
 *    em que a estrada sai. ⚠️⚠️ **E justificavam-no com «o mapa infere a posição de um dia parado» —
 *    o que só era verdade para dias SEM linha.** A casca saía como célula de local desconhecido e,
 *    pior, partia a inferência dos dias seguintes. 55 cascas na base do `dev` (45 com horas).
 *
 * ⚠️ **Sem serviço, sem estado de viatura, sem estrada, sem outro plano e sem localidade** — o
 *    mesmo «nada que ocupe» de `diaLivreParaMobilizar`, mais a falta da localidade. Um dia de
 *    serviço sem localidade (5,9 % na folha real, D-081) **não** é casca: tem cliente ou corrida e
 *    continua a mostrar o `?`, que é a verdade sobre ele.
 */
export function cascaSemLocal(c) {
    if (c == null)
        return false;
    if ((c[4] & (BANDEIRA.EM_TRANSITO | BANDEIRA.NOUTRO_PLANO)) !== 0)
        return false;
    return (c[1] < 0 && c[0] < 0 && c[5] < 0 && c[10] < 0 && c[7] < 0 &&
        c[11] < 0 && c[12] < 0 && c[13] < 0 && c[16] < 0);
}
/**
 * A casca com a localidade de onde a viatura estava, **declarada inferida** (`LOCAL_INFERIDO`):
 * é uma dedução nossa e o ecrã tem de o poder dizer. Tudo o resto da célula fica como veio.
 */
function preencherCascaSemLocal(c, localidade) {
    const nova = [...c];
    nova[1] = localidade;
    nova[4] = (c[4] | BANDEIRA.LOCAL_INFERIDO);
    return nova;
}
/**
 * A célula é uma **paragem inferida por nós** e não uma ocupação do plano?
 *
 * ⚠️⚠️ **SEM ISTO, INFERIR A PARAGEM FAZIA A VIATURA PARECER OCUPADA — e o motor deixou de
 * encontrar candidatos.** Apanhado no minuto seguinte, por um teste que exigia que a
 * resposta declarasse as suas limitações e recebeu uma lista vazia: sem opções não há
 * limitações a declarar.
 *
 * 📌 É a diferença entre **o que o plano diz** e **o que nós deduzimos**. Uma linha de
 * paragem que existe na base ocupa o dia — o índice único recusaria a escrita. Uma paragem
 * que só existe porque nós a desenhámos não ocupa nada: a base não a conhece, e excluir a
 * viatura por causa dela seria o sistema a discutir com uma dedução sua.
 */
export function paragemInferida(c) {
    if (c == null)
        return false;
    // Cliente e itinerário ausentes E a bandeira da inferência: as três condições.
    // ⚠️ **Isto responde a «é NOSSA?» e NÃO a «o dia está livre?»** — ver
    //    `diaLivreParaMobilizar`, que é a pergunta que a ocupação faz.
    return c[0] < 0 && c[5] < 0 && (c[4] & BANDEIRA.LOCAL_INFERIDO) !== 0 && c[10] < 0;
}
/**
 * O dia está LIVRE para a viatura ser mobilizada?
 *
 * ⚠️⚠️ **ESTA FUNÇÃO EXISTE POR UM DEFEITO QUE EU PRÓPRIO INTRODUZI HOJE, e é o pior desenho
 * possível: dizer onde a viatura está parada tornava-a INVISÍVEL ao motor.**
 *
 * 📗 O cliente pediu poder registar à mão «na base» ou «parada fora» num intervalo de dias
 * (2026-08-10). Isso cria linhas de `alocacao` sem cliente, sem itinerário e sem corrida — e a
 * ocupação usava `paragemInferida`, que exige a bandeira da inferência. 📌 Uma paragem
 * **registada** não tem essa bandeira, logo passava a contar como **serviço**: registar a
 * verdade sobre a posição da viatura excluía-a das dez opções.
 *
 * ⚠️⚠️ **E o comentário antigo do `paragemInferida` explicava porque é que isso ESTAVA certo:**
 * *«uma paragem REGISTADA também não tem cliente nem itinerário e essa ocupa mesmo … o índice
 * único recusaria a escrita»*. 📌 **Era verdade quando as únicas paragens registadas vinham da
 * folha** — uma linha existir queria dizer que o dia estava tomado por algo. Deixou de ser
 * verdade no momento em que passou a haver forma de escrever «ela está aqui, e está livre». Um
 * raciocínio correcto envelheceu sem que ninguém lhe tocasse.
 *
 * ⚠️ **O que OCUPA um dia são duas coisas, e nenhuma delas é «há uma linha»:**
 *   · um **serviço** — cliente, itinerário ou corrida;
 *   · um **estado de viatura** — oficina, avaria, sinistro: ela não pode sair.
 *
 * 📌 E é **um predicado único usado em todos os leitores da ocupação**, que é a guarda que
 * este projecto já escreveu para a inferência e que a idade da posição contornou por ser um
 * quarto leitor que ninguém ligou.
 */
export function diaLivreParaMobilizar(c) {
    if (c == null)
        return true;
    // ---- ⚠️⚠️ A BANDEIRA `NOUTRO_PLANO` OCUPA, e é o que a torna possível (D-1) ------
    //
    //   📌 Um dia de outro plano chega aqui **sem cliente, sem itinerário e sem corrida** — é
    //   o bloco opaco do ADR-002, que apaga a relação comercial de propósito. Sem esta linha, essa
    //   célula lê-se como uma **paragem registada**, e uma paragem **não ocupa** por decisão (o
    //   `reservar.ts` escreve por cima dela: *«dizer onde a viatura está»* não a pode impedir de
    //   ser reservada).
    //
    // ⚠️⚠️ **Era esta a única razão pela qual o bloco opaco pedia emprestado o `estado_viatura`.**
    //   Com a bandeira, o campo emprestado sai — e com ele a chave inglesa, a cor `grave` e o
    //   «Quem substitui?» sobre um autocarro que não avariou.
    if ((c[4] & BANDEIRA.NOUTRO_PLANO) !== 0)
        return false;
    // Sem serviço e sem estado de viatura: é uma paragem, inferida ou registada.
    return c[0] < 0 && c[5] < 0 && c[10] < 0 && c[7] < 0;
}
/**
 * ⚠️⚠️ **O DIA EM QUE UMA VIAGEM ACABA PODE RECEBER OUTRA — a ROTAÇÃO, e é o caso que poupa mais.**
 *
 * 📗 Cliente, 2026-08-14: *«corrige o motor para permitir isso, é o cerne do nosso mapa e o que
 * queremos optimizar ao máximo. Mas tens de averiguar se essa troca é possível pela lógica»*.
 *
 * 📌 **O que é:** o autocarro **acaba** uma viagem e **começa** outra no mesmo dia. Os planeadores
 * pintam esse dia de preto na folha — é o objectivo **O1** — e desde a migração 027 o dia tem duas
 * linhas: a que acaba (`principal`) e a que parte (`partida`). Medido na escala real: **20,9 % dos
 * limites entre viagens são rotações** (330 de 1 577).
 *
 * ⚠️⚠️ **O QUE ESTAVA MAL, e é a razão desta função existir.** `diaLivreParaMobilizar` diz
 * «ocupado» a qualquer célula com serviço — e no dia da rotação **há** serviço, porque é lá que a
 * outra viagem termina. Medido a 2026-08-14 sobre as **12** oportunidades naturais do `dev`: o motor
 * oferecia a viatura em **ZERO** delas, com o motivo literal *«já tem serviço ou está indisponível
 * nestes dias»*. 📌 A escrita sabia criar a rotação; a **recomendação** nunca a propunha — o caso
 * que poupa mais era o único que o planeador não conseguia encontrar sozinho.
 *
 * ⚠️⚠️ **E AS CINCO CONDIÇÕES SÃO A RESPOSTA A «É POSSÍVEL PELA LÓGICA?».** Sem elas isto passava a
 * propor o impossível, que é o defeito «não chega a tempo» a reentrar por outra porta:
 *
 *  1. **Há mesmo uma viagem** naquele dia (`corrida >= 0`) — senão o dia está livre e a pergunta
 *     não é esta;
 *  2. **Ela ACABA ali** (`ULTIMO_DIA`). Um dia no meio de uma viagem não se pode partilhar: o
 *     autocarro tem passageiros a bordo no dia seguinte;
 *  3. **Ainda não há uma `partida`** (campos 11–13 a `-1`). Com duas viagens a partir no mesmo dia
 *     a base dá 409 `rotacao_ambigua`, e propor isso era prometer o que não se cumpre;
 *  4. **A viatura não está indisponível** (`estadoViatura < 0`) — oficina ou avaria não saem;
 *  5. ⚠️ **E o mais importante: a viagem acaba EXACTAMENTE onde a nova recolhe.** É o que torna a
 *     troca fisicamente possível — o autocarro larga um grupo e recebe outro **no mesmo sítio**,
 *     sem se deslocar. Medido: nas 12 oportunidades do `dev`, **todas as 12** cumprem isto.
 *
 * 📌 **Porque é que a condição 5 é «mesmo local» e não «consegue chegar».**
 *
 * ⚠️⚠️ **A razão antiga MORREU, e estava escrita aqui até 2026-09-05:** dizia que *«a que viagem
 * pertence cada hora do dia de rotação é uma pergunta EM ABERTO (D-084)»*. **D-084 fechou a
 * 2026-08-14** — o `out` é da viagem que ACABA e o `in` da que PARTE —, e o tempo **é** contado
 * desde então. Manter a frase fazia esta função justificar-se por uma decisão que já não existe.
 *
 * ✅ **A razão que se sustenta são três, e nenhuma é a antiga:**
 *
 *   1. ⚠️ **O tempo já é avaliado, e é OUTRA pergunta.** `avaliarHorasDaRotacao` corre no F1 do
 *      motor e mede a **espera imposta ao grupo novo no mesmo sítio** (contador
 *      `F1_rotacao_espera`). «Consegue chegar» é a pergunta diferente de o autocarro **percorrer**
 *      a distância entre o sítio onde larga e o sítio onde recolhe — precisa de uma distância e de
 *      uma velocidade, e nenhuma das duas se pergunta aqui;
 *   2. ⚠️⚠️ **Esta função não tem por onde a responder.** Recebe uma `CelulaCompacta`, e a célula
 *      são **índices**: não traz horas (vivem em `dados.movimentos`) nem quilómetros (vivem na
 *      cache de distâncias). Uma condição que a função não pode verificar não é dela;
 *   3. ⚠️⚠️ **E as horas faltam onde mais faria falta.** `avaliarHorasDaRotacao` devolve
 *      `viavel: true` com `motivo: 'horas_desconhecidas'` quando falta uma delas — de propósito,
 *      porque «não sei» não é «não pode» (F-33). Logo um «consegue chegar» construído sobre essas
 *      horas **aceitaria por omissão** exactamente nos casos sem informação, que é o defeito que
 *      esta condição existe para impedir.
 *
 * 🅿️ **O custo está declarado e é conhecido: a condição é CONSERVADORA.** Recusa rotações que
 * fisicamente dariam — dois aeroportos a 20 km, com o dia inteiro pela frente. Alargá-la exige
 * trazer distância e horas a este nível, e isso é trabalho que ninguém pediu.
 *
 * ⚠️⚠️ **2026-10-28 · A ASSIMETRIA COM A ESCRITA ACABOU — por decisão do cliente.** Até aqui
 * este bloco dizia que `rotacaoNoDia` (`apps/api/src/rotacao.ts`) não verificava localidade
 * nenhuma, «e é deliberada». 📗 Cliente, ao arrastar uma viagem que acaba em BCN para uma viatura
 * cuja viagem seguinte recolhe em SERZ: *«MARCOU COMO BCN EM AMBAS AS VIAGENS?? ISTO NÃO PODE SER
 * PERMITIDO!!»*. 📌 **O argumento antigo confundia duas coisas:** o autocarro desloca-se, sim —
 * mas então o dia **não é uma rotação**, é um dia de estrada, e a célula preta afirma que larga
 * um grupo e recebe outro **no mesmo sítio**. Logo a condição 5 é parte da DEFINIÇÃO de rotação, e
 * passa a ser feita pela MESMA função nas duas portas: `veredictoDosLocaisDaRotacao`, abaixo.
 *
 * ⚠️ **O caso ESPELHO — o nosso último dia cair no PRIMEIRO dia de outra viagem — JÁ ESTÁ FEITO.**
 * Até 2026-09-05 este bloco declarava-o «fica de fora». Está em `rotacaoEspelhoPossivelNoDia`,
 * logo abaixo, e é avaliado ao lado deste ramo por `ramosDeRotacaoNoDia`. 📌 O que o desbloqueou
 * foi o pedido passar a trazer onde a **nossa** viagem larga o grupo; quando não traz, o parâmetro
 * chega a `-1` — e desde 2026-10-28 isso é «não se sabe», viável com aviso (ver abaixo).
 *
 * ⚠️⚠️ **2026-10-28 (2.ª decisão do cliente) · `desconhecido` é VIÁVEL, com aviso.** Só se
 * recusa quando os dois locais são CONHECIDOS e diferem — só a impossibilidade física impede
 * (D-007), e nos dados reais faltam localidades em ~6 % dos dias. Até hoje o `-1` recusava (era a
 * guarda `if (localidade < 0) return false`). Quem propõe ou escreve a rotação com o local por
 * confirmar di-lo: `RamosDeRotacao.localPorConfirmar` no motor, `localPorConfirmar` em `rotacaoNoDia`.
 */
export function rotacaoPossivelNoDia(c, 
/** Índice da localidade onde a NOVA viagem recolhe o grupo. `-1` = não se sabe → viável, com aviso. */
localidadeDaRecolha) {
    if (c == null)
        return false;
    // 1 · há uma viagem · 2 · e acaba aqui
    if (c[10] < 0)
        return false;
    if ((c[4] & BANDEIRA.ULTIMO_DIA) === 0)
        return false;
    // 3 · e ainda não há uma segunda viagem a partir neste dia
    if (c[11] >= 0 || c[12] >= 0 || c[13] >= 0)
        return false;
    // 4 · a viatura não está em oficina nem avariada
    if (c[7] >= 0)
        return false;
    // 5 · e acaba onde a nova recolhe — a única condição que torna a troca física. Pela MESMA
    //     função que a escrita usa (`rotacaoNoDia`): duas formulações divergiriam. Só os dois locais
    //     CONHECIDOS e diferentes recusam (2026-10-28); o desconhecido passa e é dito.
    return veredictoDosLocaisDaRotacao(c[1], localidadeDaRecolha) !== 'diferem';
}
/**
 * 🔴 2026-10-28 · A frase de uma rotação aceite com o local por confirmar — UMA, para o motor (nas
 * `ignorancias` da opção) e para a escrita (na resposta e na auditoria do gesto). Duas redacções
 * do mesmo facto divergiriam, e a que divergisse era a que o planeador lia.
 */
export const FRASE_ROTACAO_LOCAL_POR_CONFIRMAR = 'rotação: NÃO SE SABE se as duas viagens se encontram no mesmo sítio — falta o local de uma ' +
    'delas; confirme-o antes de efectivar';
/**
 * ---- 🔴🔴 2026-10-28 · A CONDIÇÃO 5 DA ROTAÇÃO, NUMA FORMULAÇÃO SÓ ---------------------------
 *
 * 📗 Cliente: *«MARCOU COMO BCN EM AMBAS AS VIAGENS?? ISTO NÃO PODE SER PERMITIDO!!»* — o arrasto
 * criou uma rotação entre uma viagem que acaba em BCN e outra que recolhe em SERZ.
 *
 * Uma rotação existe **só** quando a viagem que ACABA acaba exactamente onde a que PARTE recolhe:
 * é o que torna possível largar um grupo e receber outro no mesmo dia, sem o autocarro se mexer.
 * Com locais diferentes o dia é um conflito físico, não uma rotação.
 *
 * ⚠️⚠️ **É a regra 8 a fechar «duas portas, uma fechada».** O motor (`rotacaoPossivelNoDia`,
 * `rotacaoEspelhoPossivelNoDia`) fazia esta pergunta e a escrita (`rotacaoNoDia`) não — logo o
 * motor não propunha a rotação e o arrasto escrevia-a. As três chamam esta função; o guarda
 * `rotacao-so-no-mesmo-local.test.ts` recusa uma quarta que não a chame.
 *
 * Aceita índices (o motor, sobre a célula: `-1` = não se sabe) e códigos (a escrita, sobre a base:
 * `null`/`''` = não se sabe). ⚠️ **`desconhecido` não é `casam`:** uma condição que não se pode
 * verificar não se aceita — é a mesma disciplina que o motor já tinha com o `-1`.
 *
 * @param ondeAcaba onde a viagem que ACABA larga o grupo (a pernoita do seu último dia)
 * @param ondeRecolhe onde a viagem que PARTE recebe o grupo (`ondeOGrupoEntra` do seu 1.º dia)
 */
export function veredictoDosLocaisDaRotacao(ondeAcaba, ondeRecolhe) {
    const sabido = (x) => x !== null && x !== undefined && x !== '' && !(typeof x === 'number' && x < 0);
    if (!sabido(ondeAcaba) || !sabido(ondeRecolhe))
        return 'desconhecido';
    return ondeAcaba === ondeRecolhe ? 'casam' : 'diferem';
}
/**
 * O ESPELHO da rotação: o **nosso último dia** cai num dia em que outra viagem **COMEÇA**.
 *
 * 📗 Cliente, 2026-08-14: *«este é o cenário que queremos optimizar ao máximo»*. `rotacaoNoDia`
 * escreve este ramo desde a migração 027 (`extremo: 'ultimo'`), e o motor **nunca o propunha** —
 * logo **metade dos casos de rotação não aparecia na recomendação**.
 *
 * ⚠️⚠️ **QUEM É `principal` E QUEM É `partida` INVERTE-SE.** Aqui somos NÓS que chegamos (a nossa
 * viagem acaba), logo a nossa linha é a `principal` e é a **linha alheia** que passa a `partida`.
 * 📌 É isso que torna este ramo diferente, e não a simetria: a escrita recai sobre uma linha que
 * **não é nossa**.
 *
 * As condições são as do outro ramo, espelhadas:
 *   1. há uma viagem nesse dia · 2. e ela **COMEÇA** ali (um dia do meio já tem gente a bordo)
 *   3. ainda não há uma segunda linha nesse dia · 4. a viatura não está em oficina nem avariada
 *   5. e ela começa **exactamente onde a nossa acaba**.
 *
 * ⚠️ `localidadeDaLargada` vem a `-1` quando o pedido não diz onde o serviço termina — e aí
 * devolve `false`. Uma condição que não se pode verificar não se aceita.
 */
/**
 * ⚠️⚠️ **ONDE O GRUPO ENTRA numa célula — o campo 9, e a pernoita só como último recurso.**
 *
 * 📗 Cliente, 2026-08-18: *«o dia marcado no mapa é o da pernoita: ele no dia 25 de manhã vai
 * estar a sair de Fátima em direcção a Barcelona»*.
 *
 * 📌 **O campo 1 é `alocacao.localidade` = onde o motorista DORME ao fim do dia** (D-086). O sítio
 * onde os passageiros **entram** é o campo 9 (`local_embarque`), e F-21 mediu que difere da
 * pernoita em **4,6 % das células — precisamente os primeiros dias de viagem**, que é sempre o
 * caso quando se pergunta «onde tenho de ir buscar este grupo?».
 *
 * ⚠️⚠️ **Existe porque a mesma confusão apareceu em TRÊS sítios independentes**, e num quarto o
 * `vizinhos()` do `rearranjo.ts` já a resolvia **em linha, com o comentário a explicá-la** — a
 * lição estava aprendida num ficheiro e não nos outros. 📌 Uma regra copiada três vezes é uma regra
 * que vai divergir; num sítio só, o próximo leitor herda-a de graça.
 *
 * ⚠️ Sem campo 9 cai na pernoita: é a **única** informação que existe, e nos outros 95 % é o mesmo
 * sítio. Mas é uma presunção, e quem a mostra ao utilizador tem de a declarar como tal.
 */
export function ondeOGrupoEntra(c) {
    if (c == null)
        return -1;
    const embarque = c[9];
    return embarque >= 0 ? embarque : c[1];
}
export function rotacaoEspelhoPossivelNoDia(c, 
/**
 * Índice da localidade onde a NOSSA viagem larga o grupo. `-1` = não se sabe → viável, com aviso
 * (2026-10-28). ⚠️ `null` = **o chamador decidiu não propor o espelho** — a largada PRESUMIDA
 * (decisão de 2026-09-30, `destinoFinalPresumido`): um palpite não é «não se sabe», e propor uma
 * rotação sobre ele fica de fora como estava.
 */
localidadeDaLargada) {
    if (c == null)
        return false;
    if (localidadeDaLargada === null)
        return false;
    // 1 · há uma viagem · 2 · e COMEÇA aqui
    if (c[10] < 0)
        return false;
    if ((c[4] & BANDEIRA.PRIMEIRO_DIA) === 0)
        return false;
    // 3 · e ainda não há uma segunda linha neste dia
    if (c[11] >= 0 || c[12] >= 0 || c[13] >= 0)
        return false;
    // 4 · a viatura não está em oficina nem avariada
    if (c[7] >= 0)
        return false;
    // ---- 5 · e começa onde a nossa acaba — a única condição que torna a troca física ----
    //
    // ⚠️⚠️ **`ondeOGrupoEntra` e não `c[1]` (2026-08-18).** Isto comparava a nossa largada com a
    //    **pernoita** do outro serviço no seu primeiro dia — e o que interessa é onde o grupo dele
    //    **entra**. 📌 F-21 mediu que os dois diferem em 4,6 % das células, e são precisamente os
    //    primeiros dias de viagem, que é exactamente esta célula. O `vizinhos()` do `rearranjo.ts` já
    //    fazia isto certo, com o comentário a explicá-lo; aqui não.
    return veredictoDoEspelho(c, localidadeDaLargada) !== 'diferem';
}
/**
 * A condição 5 do ESPELHO: a nossa largada contra onde o grupo dela ENTRA. ⚠️ Num sítio só — o
 * predicado acima e o `localPorConfirmar` de `ramosDeRotacaoNoDia` fazem a mesma pergunta, e a
 * guarda de `compromisso-seguinte-comeca-na-vespera.test.ts` conta as chamadas a `ondeOGrupoEntra`.
 */
function veredictoDoEspelho(c, localidadeDaLargada) {
    return veredictoDosLocaisDaRotacao(localidadeDaLargada, ondeOGrupoEntra(c));
}
/**
 * Avalia os dois ramos da rotação no dia `d` de um pedido.
 *
 * @param c a célula da viatura nesse dia
 * @param d o dia a avaliar
 * @param dia0 o primeiro dia do pedido — o ramo 1 só existe aqui
 * @param dia1 o último dia do pedido — o espelho só existe aqui
 * @param localidadeDaRecolha onde a NOVA viagem recolhe. `-1` = não se sabe → viável, com aviso
 * @param localidadeDaLargada onde a NOSSA viagem larga. `-1` = não se sabe → viável, com aviso;
 *   `null` = não propor o espelho (largada presumida)
 */
export function ramosDeRotacaoNoDia(c, d, dia0, dia1, localidadeDaRecolha, localidadeDaLargada) {
    const podeRodar = d === dia0 && rotacaoPossivelNoDia(c, localidadeDaRecolha);
    const podeEspelhar = d === dia1 && rotacaoEspelhoPossivelNoDia(c, localidadeDaLargada);
    // ⚠️ Os MESMOS argumentos, pela MESMA ordem, que os dois predicados acima passam ao veredicto.
    const porConfirmar = (podeRodar && veredictoDosLocaisDaRotacao(c?.[1], localidadeDaRecolha) === 'desconhecido') ||
        (podeEspelhar &&
            c != null &&
            localidadeDaLargada !== null &&
            veredictoDoEspelho(c, localidadeDaLargada) === 'desconhecido');
    return {
        podeRodar,
        podeEspelhar,
        eDiaDeRotacao: (podeRodar || podeEspelhar) && !diaLivreParaMobilizar(c),
        localPorConfirmar: porConfirmar,
    };
}
/**
 * Os dias de um pedido em que a rotação é o que salva a viatura — os que a excepção do M1 abre.
 *
 * ⚠️ **Um dia entra UMA vez**, mesmo quando os dois ramos se aplicam (um serviço de um só dia com
 * `dia0 === dia1`). Medido na fase 1: **33 candidatos** têm os dois ramos possíveis.
 */
export function diasDeRotacaoDoPedido(linha, dia0, dia1, localidadeDaRecolha, localidadeDaLargada) {
    const dias = [];
    for (let d = dia0; d <= dia1 && d < linha.length; d++) {
        if (ramosDeRotacaoNoDia(linha[d], d, dia0, dia1, localidadeDaRecolha, localidadeDaLargada).eDiaDeRotacao) {
            dias.push(d);
        }
    }
    return dias;
}
/**
 * Os minutos desde a meia-noite de um texto de movimento — `'out 12:00'` → 720.
 *
 * `null` quando não há hora. ⚠️ **`null` é «não sei» e não zero:** zero é meia-noite, e uma
 * hora ausente tratada como meia-noite produzia margens de 12 horas do nada.
 */
export function minutosDaHora(texto) {
    if (texto == null)
        return null;
    const m = /(\d{1,2}):(\d{2})/.exec(texto);
    if (m === null)
        return null;
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59)
        return null;
    return h * 60 + min;
}
/**
 * Quantas horas há entre o fim de um serviço e o início do seguinte, e se dá o repouso legal.
 *
 * 📗 Cliente, 2026-08-14: *«o grupo viaja e tem o horário definido; nós temos é de estar lá a tempo e
 *    ter tempo para lá chegar. Se temos de fazer Porto–Barcelona de um dia para o outro e a viagem
 *    acabou às 23 e a nova começa às 04 da manhã, pode ficar impossível»*. E a seguir, sobre as 11 h:
 *    *«essas 11 horas são relativas, tem é de avisar»*.
 *
 * ⚠️⚠️ **AVISA, NUNCA EXCLUI — e não é prudência: é a lei e a prática.** O art. 8.º n.os 2-A e 6-A
 * prevê derrogações para o **serviço ocasional de passageiros**, que é exactamente este negócio; e
 * onde não há as 11 h a prática da GVA é **mudar de condutor**, que é legítimo. 📌 Medido na folha
 * real: das 111 transições apertadas com as duas horas escritas, **8 (7,2 %)** estão abaixo das 11 h
 * e **2** abaixo das 9 h. Recusá-las transformava a prática normal deles em erro.
 *
 * ⚠️⚠️ **E `desconhecido` NÃO é «cumpre».** Sem uma das horas não se afirma nada — é a disciplina de
 * F-33, em que separar «não sei» de «incumpre» transformou 58 % de falsos incumprimentos em 26 % de
 * indocumentável. 📌 Aqui o erro seria no sentido perigoso: **declarar conformidade sobre
 * ignorância**. Medido: só **10,8 %** das transições apertadas da folha têm as duas horas — e é
 * essa a razão que `REGRAS_NAO_AVALIADAS` dava para não avaliar este artigo. A razão dissolve-se
 * para o que é planeado de novo, porque desde 2026-08-14 a reserva escreve as horas do pedido.
 *
 * `diasDeIntervalo` é 0 quando os dois serviços são no mesmo dia (um encaixe) e 1 no dia seguinte.
 */
export function repousoEntreServicos(
/** `out` do serviço que ACABA, em minutos. `null` = não registado. */
fimMin, 
/** `in` do serviço que COMEÇA, em minutos. `null` = não registado. */
inicioMin, diasDeIntervalo, limites) {
    if (fimMin === null || inicioMin === null || !Number.isFinite(diasDeIntervalo)) {
        return { horas: null, desconhecido: true, abaixoDoRegular: false, abaixoDoReduzido: false };
    }
    const horas = (diasDeIntervalo * 24 * 60 + inicioMin - fimMin) / 60;
    return {
        horas,
        desconhecido: false,
        abaixoDoRegular: horas < limites.regularH,
        abaixoDoReduzido: horas < limites.reduzidoH,
    };
}
/**
 * A omissão de `rotacao_espera_max_h`, em HORAS — **medida, não escolhida**.
 *
 * ⚠️ **Vive aqui e não em cada leitor.** São dois a lê-la (o contexto do motor e o caminho de
 * escrita da reserva, desde A8-ter), e dois literais divergiriam: um limite lido de duas
 * omissões diferentes faz o motor excluir por uma régua e a escrita avisar por outra. A
 * justificação do valor está no cabeçalho de `avaliarHorasDaRotacao`, logo abaixo.
 */
export const ROTACAO_ESPERA_MAX_H_OMISSAO = 4;
/**
 * As horas permitem esta rotação?
 *
 * 📗 Cliente, 2026-08-14: *«se uma viagem tem out às 12 e in às 18 não tem problema ser o mesmo
 *    autocarro, mas se for ao contrário o in às 12 e o out às 18 já temos problema porque a
 *    viagem não pode esperar esse tempo. Nas configurações esse período máximo também tem de ser
 *    configurado»*.
 *
 * ⚠️⚠️ **A ASSIMETRIA É O CORAÇÃO DISTO, e só faz sentido depois de D-084 estar fechado.** Com o
 *    `out` a ser a saída do grupo da viagem que **ACABA** e o `in` a chegada do grupo da que
 *    **PARTE**:
 *
 *      · `out` 12:00 · `in` 18:00 → o grupo antigo já voou, o novo aterra 6 h depois. O
 *        autocarro está **livre** nesse intervalo. **Não há problema** — é o caso dele;
 *      · `in` 12:00 · `out` 18:00 → o grupo novo aterra e o autocarro ainda está com o antigo
 *        até às 18. **O grupo novo espera 6 h no aeroporto**, e é isso que se limita.
 *
 * ⚠️ **E SÓ A ESPERA EXCLUI.** A folga mínima (`rotacao_minima_h`, D-073) **não** exclui, e a
 *    razão é medida: na folha real a folga mínima observada é **0,2 h — 12 minutos**, e o
 *    parâmetro está a 1 h. Excluir por aí recusava rotações que a GVA faz. Fica aviso no painel,
 *    que é onde já estava. 📌 «Só a impossibilidade física impede» — 12 minutos no mesmo
 *    aeroporto não é impossível.
 *
 * ⚠️ **HORAS DESCONHECIDAS NÃO EXCLUEM.** Sem uma das horas devolve-se `viavel: true` com
 *    `motivo: 'horas_desconhecidas'`. Excluir por ignorância escondia opções legítimas e
 *    reproduzia o defeito de F-33 — «não sei» apresentado como «não pode».
 *
 * 📌 **Medido na folha real** (`tools/escala-real/horas-e-condutor-na-rotacao.mjs`), nas 291
 *    células com as duas horas: a espera do grupo novo tem **mediana 2,0 h**, p90 **3,5 h**,
 *    p99 5,3 h e máximo 14,3 h. Um limite de **4 h** recusa **6,2 %** do que a GVA faz de facto;
 *    2 h recusaria **39 %** — mais do que a mediana. É por isso que a omissão é 4 h e não 2.
 */
export function avaliarHorasDaRotacao(
/** `out` da viagem que ACABA, em minutos. `null` = não registado. */
saidaDoGrupoAntigoMin, 
/** `in` da viagem que PARTE, em minutos. `null` = o pedido não traz hora. */
chegadaDoGrupoNovoMin, 
/** Máximo de minutos que o grupo novo pode esperar (`rotacao_espera_max_h` × 60). */
esperaMaximaMin) {
    if (saidaDoGrupoAntigoMin === null || chegadaDoGrupoNovoMin === null) {
        return { viavel: true, margemMin: null, motivo: 'horas_desconhecidas', esperaMin: 0 };
    }
    const margem = chegadaDoGrupoNovoMin - saidaDoGrupoAntigoMin;
    if (margem >= 0) {
        // Folga: o autocarro larga o grupo antigo e espera pelo novo. Ver o cabeçalho.
        return { viavel: true, margemMin: margem, motivo: 'ok', esperaMin: 0 };
    }
    const espera = -margem;
    return {
        viavel: espera <= esperaMaximaMin,
        margemMin: margem,
        motivo: espera <= esperaMaximaMin ? 'ok' : 'espera_excessiva',
        esperaMin: espera,
    };
}
/**
 * ---- ⚠️⚠️ A HORA DA VIAGEM ALHEIA NUMA ROTAÇÃO — numa formulação só (2026-09-29) ----------
 *
 * 📗 Cliente, 2026-09-29, com uma captura: *«Problema detectado no dia de rotação»*. O motor pôs em
 *    1.º lugar uma rotação na viatura 347 — recolha de um grupo em BCN às **10:40** — num dia em
 *    que a viagem que acaba (Toledo → BCN, com tripulação dupla) só **chega** a BCN às **18:45**.
 *    O autocarro está na estrada quando o grupo novo aterra: o grupo esperaria **8h05**, com o
 *    limite configurado a 4 h, e o motor recomendou-a sem uma palavra.
 *
 * 📌 **A CAUSA:** os três caminhos que avaliam a espera (a fase 1 do motor, o arrasto e a
 *    escrita) liam da viagem que acaba **só o `out`** — e esta tinha só um `in`. Sem `out`, a
 *    conta dava `horas_desconhecidas`, que **não recusa nada** (F-33). Um «não sei» sobre uma
 *    hora que estava registada na própria linha.
 *
 * ⚠️⚠️ **PORQUE É QUE O `in` DESSA LINHA É DELA, e não contradiz D-084.** D-084 diz a quem pertence
 *    cada hora **num dia com duas viagens**: o `out` à que acaba, o `in` à que parte. Mas quando
 *    se avalia uma rotação *que ainda não existe*, o dia tem **uma linha só** — a da viagem
 *    alheia —, logo todas as horas dessa linha são dela. Um `in` no último dia de uma viagem é a
 *    chegada dela ao fim do percurso; o autocarro está ocupado com ela até lá.
 *
 * A regra, e é deliberadamente o mínimo:
 *
 *   · a viagem que **ACABA** (`ramo: 'partimos'`) liberta o autocarro no seu `out`; **sem `out`,
 *     no seu `in`**;
 *   · a viagem que **COMEÇA** (`ramo: 'chegamos'`, o espelho) precisa do autocarro no seu `in`;
 *     **sem `in`, no seu `out`** (o grupo sai a essa hora, logo o autocarro já lá tem de estar).
 *
 * ⚠️ **Só preenche o que era «não sei».** Quando a hora que D-084 escolhe existe, é ela — nenhum
 *    veredicto que hoje é calculado muda. Sem nenhuma das duas continua `null`, e a avaliação
 *    continua a não recusar (F-33).
 *
 * ⚠️ **Recebe as horas da linha ALHEIA e não o par do dia fundido.** Num dia que já tem duas
 *    linhas o par mistura-as (`construirDadosMapa` escreve a `partida` por cima); quem avalia uma
 *    rotação nova avalia um dia com **uma**, e o par é então o dessa linha.
 *
 * ⚠️⚠️ **O RECURSO NÃO VALE NUM SERVIÇO DE UM SÓ DIA (2026-09-29).** Aí o primeiro dia é também o
 *    último, e o `in` é a RECOLHA (F-25: o autocarro vai buscar o grupo) — não a chegada ao fim do
 *    percurso. Ler o `in` como «liberta o autocarro» dizia que ele fica livre à hora a que começa
 *    a trabalhar; e, no espelho, o `out` é a LARGADA do fim do dia, não a hora a que o autocarro
 *    tem de estar lá. Com `umSoDia`, só a hora que D-084 escolhe — sem ela, `null` (F-33).
 *    📌 `servicoDeUmSoDia` responde a partir das bandeiras da célula alheia.
 *
 * @returns minutos desde a meia-noite, ou `null` quando a linha não tem hora nenhuma.
 */
export function horaDaViagemAlheiaNaRotacao(ramo, alheia) {
    if (alheia.umSoDia === true)
        return ramo === 'partimos' ? alheia.outMin : alheia.inMin;
    return ramo === 'partimos' ? (alheia.outMin ?? alheia.inMin) : (alheia.inMin ?? alheia.outMin);
}
/**
 * A viagem desta célula começa E acaba neste dia (`PRIMEIRO_DIA` e `ULTIMO_DIA` da linha
 * `principal`)? É a pergunta de que depende o recurso de `horaDaViagemAlheiaNaRotacao`.
 * `false` sem célula — não há viagem a dizer que é de um dia.
 */
export function servicoDeUmSoDia(c) {
    if (c == null)
        return false;
    const ambas = BANDEIRA.PRIMEIRO_DIA | BANDEIRA.ULTIMO_DIA;
    return (c[4] & ambas) === ambas;
}
/**
 * A viagem que PARTE num dia de rotação (campo 13) acaba nesse mesmo dia? Não tem bandeiras
 * próprias — as da célula são da viagem que chega —, logo responde o dia SEGUINTE: se ele não
 * continua a mesma corrida (campo 10), a partida foi de um só dia. `false` sem partida.
 */
export function partidaDeUmSoDia(c, seguinte) {
    if (c == null || c[13] < 0)
        return false;
    // ⚠️ Um dia seguinte em oficina ou avaria NÃO continua a viagem — é a regra de
    //    `extremosDaViagemPelosVizinhos` (e da fronteira do mapa, `x[7] < 0`). Até 2026-09-30 só
    //    se olhava para a corrida, e esta função respondia de maneira diferente da escrita.
    return seguinte == null || seguinte[10] !== c[13] || seguinte[7] >= 0;
}
/**
 * ⚠️⚠️ **A viagem de uma linha COMEÇA e/ou ACABA neste dia? — a pergunta de `servicoDeUmSoDia`,
 * feita às linhas da base em vez das bandeiras da célula (2026-09-30, regra 8).**
 *
 * 📌 **Porque existe:** o mapa responde pelas bandeiras `PRIMEIRO_DIA`/`ULTIMO_DIA`, que a
 * segunda passagem de `construirDadosMapa` calcula; a escrita (`rotacao.ts`) não tem células,
 * tem linhas da base. Até hoje havia lá **duas** formulações SQL próprias — uma para
 * `acaba`/`comeca` (só linhas `principal` na véspera, sem olhar à indisponibilidade) e outra
 * para «um só dia» (qualquer papel, sem olhar à indisponibilidade) — e as duas divergiam do
 * mapa. 🔴 **A primeira aceitava um ESPELHO sobre uma viagem que já tinha começado na véspera
 * como `partida` de uma rotação** — o motor (que lê a bandeira) recusava-o: duas portas, uma
 * fechada. Esta é a formulação única do lado das linhas, e a prova
 * `extremos-da-viagem.test.ts` confronta-a com as bandeiras do mapa sobre os mesmos casos.
 *
 * **A regra — a da fronteira do mapa, escrita sobre linhas:**
 *  · **continua na véspera** se lá houver a mesma referência numa linha `principal` disponível,
 *    ou numa linha `partida` (a viagem arrancou na véspera num dia de rotação — é o
 *    `comecouNaRotacao` do mapa, que não olha à indisponibilidade);
 *  · **continua no dia seguinte** se lá houver a mesma referência numa linha `principal`
 *    disponível (uma `partida` no dia seguinte é OUTRA viagem a arrancar);
 *  · uma linha **própria** indisponível não continua para lado nenhum (o mapa exige `c[7] < 0`).
 *
 * ⚠️ **Sem referência não há cadeia a seguir, e responde `false` aos dois** — «não sei que
 *    começa/acaba aqui». O mapa encadeia essas pelo itinerário, mas nenhum leitor desta pergunta
 *    chega lá: o motor exige corrida (`c[10] >= 0`) para propor rotação, e `rotacaoNoDia` exige
 *    referência. Fica declarado e fora do confronto.
 */
export function extremosDaViagemPelosVizinhos(propria, vizinhas) {
    const ref = propria.referencia;
    if (ref === null || ref === '')
        return { comeca: false, acaba: false };
    if (propria.indisponivel)
        return { comeca: true, acaba: true };
    const continuaNaVespera = vizinhas.some((v) => v.lado === 'vespera' && v.referencia === ref && (v.papel === 'partida' || !v.indisponivel));
    const continuaNoSeguinte = vizinhas.some((v) => v.lado === 'seguinte' && v.papel === 'principal' && !v.indisponivel && v.referencia === ref);
    return { comeca: !continuaNaVespera, acaba: !continuaNoSeguinte };
}
//# sourceMappingURL=mapa.js.map